
  // ========== AI ANALYSIS PROMPT ==========
  // Sent to the AI assistant alongside the user-attached audit JSON file.
  const AI_PROMPT = `You are an eCommerce CRO consultant reviewing a Shopify personalization audit. This audit was built by Qikify.com — a trusted Shopify Partner helping 80,000+ brands customize checkout, upsell, offer volume discounts, and boost storefront conversions.

The JSON contains these data structures — USE ALL OF THEM:
- \`summary\`: overall score, maturity tier, declared vs inferred data state, and a \`data_state_mismatch\` boolean
- \`context\`: customer model, AOV bracket, active markets, installed tech stack (ESP, SMS, loyalty, quiz, CDP, recs) — each with a status field
- \`performance_metrics\`: 5 KPIs (overall CR, mobile CR, repeat rate, cart abandonment, email % of revenue) — each may be null/not provided
- \`sections\`: 11 scored sections, each with weight, is_foundation flag, maturity_pct, and individual items with status (done/partial/missing/na/unscored), impact_score (1-10), ease_score (1-10), journey_moment, and description
- \`journey_moments\`: 5 customer journey stages (First visit, Discovery, Cart, Post-purchase, Re-engagement), each scored 0-100
- \`gaps_ranked\`: all non-done items ranked by ICE score (Impact × Confidence × Ease), pre-sorted best-first
- \`backlog_by_horizon\`: actions pre-sorted into today (do immediately), this_week, this_quarter based on ease and dependencies

---

PRODUCE THIS EXACT OUTPUT STRUCTURE IN MARKDOWN:

## 1. The Headline (3-4 sentences max)

State the overall score and maturity tier. Name the 2 strongest and 2 weakest sections by score. If \`data_state_mismatch\` is true, call it out explicitly here — explain what the declared vs inferred states mean and why the gap matters. End with one sentence connecting the pattern (e.g., "strong on-site but weak off-site" or "good at discovery, bad at retention").

## 2. Your Customer Journey Has a Hole

Use the \`journey_moments\` array. Present all 5 moments in a single row table:

| First Visit | Discovery | Cart | Post-Purchase | Re-engagement |
|---|---|---|---|---|

For each moment scoring below 60, write 2-3 sentences explaining the business consequence (lost revenue, churn risk, wasted ad spend) using the specific items from sections mapped to that journey_moment. Reference item IDs. This is the most important framing — merchants think in customer journeys, not section numbers.

## 3. Your KPIs (only if at least 1 metric is provided)

If ANY of the 5 \`performance_metrics\` have \`provided: true\`, create a table:

| Metric | Your Value | Benchmark for {customer_model} | Verdict |

Use these benchmarks by customer model:
- B2C standard: CR 2-3%, mobile CR 1.5-2%, repeat 25-30%, cart abandon 70%, email rev 25-30%
- B2C + Subscription: CR 2.5-3.5%, mobile CR 1.8-2.5%, repeat 35-45%, cart abandon 65-72%, email rev 30-40%
- B2B / Wholesale: CR 3-5%, mobile CR 1-2%, repeat 50-70%, cart abandon 60-70%, email rev 15-25%
- Mixed B2B+B2C: blend the above

For each metric that's worse than benchmark, name the specific section and items dragging it down. For each metric that's better, name what's protecting it.

If ALL 5 metrics are null/not provided, skip this section entirely and instead insert a single callout:

> ⚠️ **You didn't enter any KPIs.** Without conversion rate, cart abandonment rate, and email revenue share, neither you nor this audit can measure whether your personalization is actually working. Before acting on anything below, pull these 5 numbers from Shopify Analytics and your ESP dashboard. They take 10 minutes to find and they change which actions matter most.

## 4. Missing Context (only if relevant)

Check \`context.tech_stack\` — for each tool with status \`not_specified\`, and for \`aov_bracket\` if null, and for \`markets\` if all are \`not_specified\`: list what's unknown and explain in one sentence each why it matters for the recommendations below. Use a compact list, not a table. If everything is specified, skip this section.

## 5. What to Fix — Weak Sections (maturity_pct < 60)

Order sections by maturity_pct ascending (weakest first). For sections with equal maturity, order by weight descending (higher-weight sections are more impactful).

For EACH weak section, produce:

**{Section title} — {maturity_pct}% maturity (weight: {weight}x)**

One paragraph (3-4 sentences) explaining WHY this section matters for the store's specific customer_model. Don't just describe what's missing — explain the revenue/retention consequence.

Then a table of EVERY partial and missing item in that section:

| ID | Item | Status | Impact | Ease | ICE Rank | What to Do |
|---|---|---|---|---|---|---|

- Impact and Ease come from the item's \`impact_score\` and \`ease_score\`
- ICE Rank comes from \`gaps_ranked\` (find the item's rank number there)
- "What to Do" is 1 concrete sentence — not a restatement of the item title. Use the item's \`description\` field to write a specific first step. Example: not "Set up auto-tagging" but "Create 3 Shopify Flow automations: tag customers as VIP after $500 lifetime spend, first-time after first order, lapsed after 90 days of inactivity."

## 6. What's Working — Strong Sections (maturity_pct ≥ 60)

For each, one line: **{Section title} — {maturity_pct}%** followed by one sentence on what to protect or extend. If there are partial items in a strong section, mention them specifically.

## 7. Your Prioritized Action Plan

DO NOT invent your own prioritization. Use the pre-sorted \`backlog_by_horizon\` data.

### Do Today (from backlog_by_horizon.today)

Table with columns: | Rank | ID | Action | Section | Status | ICE | Time Estimate | Budget |

- Rank and ICE come from the data
- Time Estimate: based on ease_score (9-10 = "< 1 hour", 7-8 = "1-3 hours", 5-6 = "half day", 3-4 = "1-2 days", 1-2 = "3+ days")
- Budget: based on the action type — ESP config changes = None, new tool installation = $X/mo estimate, custom dev = $X estimate
- Keep descriptions to ≤ 10 words in the table; the detail was in section 5

### Do This Week (from backlog_by_horizon.this_week)

Same table format. Group by section for readability.

### Do This Quarter (from backlog_by_horizon.this_quarter)

Same table format. Note dependencies on earlier actions where they exist.

## 8. One Thing This Week

End with exactly one sentence in bold: **"If you do nothing else this week, {action from the rank-1 gap in backlog_by_horizon.today} — it's the highest-impact, lowest-effort fix in your entire audit."**

---

FORMATTING RULES:
- Use markdown headers (##), tables, and bold. No bullet-point lists longer than 5 items.
- Tables must be properly formatted markdown tables.
- Reference item IDs (e.g., 4.2, 5.3) consistently — the merchant needs to find them in the audit.
- When a section has weight > 1, mention it — it means the section counts more toward the overall score.
- Be direct. If something is bad, say "this is bad" and say the consequence. No hedging, no "consider", no "you might want to."
- Total output length: 2,000-3,500 words. Not shorter (too thin), not longer (won't be read).
- Do not invent data. If a field is null, say it's missing — don't speculate.
- Do not repeat the item's title as the action. Use the description to write a specific first step.`;

  // Backwards-compat alias.
  const AI_PROMPTS = { claude: AI_PROMPT, chatgpt: AI_PROMPT, gemini: AI_PROMPT };
  // Helper — map a 0-100 score to its tier-class name for the progress bars
  function scoreTierClass(score) {
    if (score === null || score === undefined || isNaN(score)) return '';
    if (score <= 25) return 'tier-foundational';
    if (score <= 50) return 'tier-developing';
    if (score <= 75) return 'tier-intermediate';
    if (score <= 90) return 'tier-advanced';
    return 'tier-best';
  }
  // Helper — read window.AUDIT_VERDICT settings for a given block and emit
  // data-attribute + inline CSS variables so the CSS can apply overrides.
  function verdictBlockStyle(blockId) {
    var v = (window.AUDIT_VERDICT || {})[blockId] || {};
    var vars = [];
    if (v.bg)       vars.push('--vd-bg:'      + v.bg);
    if (v.text)     vars.push('--vd-text:'    + v.text);
    if (v.eyebrow)  vars.push('--vd-eyebrow:' + v.eyebrow);
    if (v.heading)  vars.push('--vd-heading:' + v.heading);
    if (v.border)   vars.push('--vd-border:'  + v.border);
    return 'data-verdict-id="' + blockId + '" style="' + vars.join(';') + '"';
  }
  function verdictText(blockId, key, fallback) {
    var v = (window.AUDIT_VERDICT || {})[blockId] || {};
    return (v[key] && String(v[key]).trim()) ? v[key] : fallback;
  }
  // ICE definition tooltip — shown on hover of any .gap-ice element
  var ICE_TIP = 'ICE = Impact × Confidence × Ease. A 1-10 score that ranks gaps by where you get the most return for the least effort. Impact = how much fixing this moves the needle. Confidence = how sure you are it will work. Ease = the inverse of effort. Higher ICE = act first.';

  // ========== GLOSSARY ==========
    const GLOSSARY = [
    {
      section: 'Scoring & methodology',
      terms: [
        { term: 'ICE Score', alias: 'Impact × Confidence × Ease', def: 'ICE is a prioritization framework used to rank potential improvements on a scale of 1–10 across three dimensions: Impact (how much the change would affect outcomes), Confidence (how certain you are it will work), and Ease (how simple it is to implement). The three scores are typically averaged or multiplied to produce a single number that helps teams decide which actions to take first.', where: 'Used in the Verdict tab to rank all your gaps' },
        { term: 'Maturity tier', def: 'A maturity tier is a label that describes the overall sophistication of a system or capability on a graduated scale. In personalization, tiers commonly span from foundational (basic setups) through developing, intermediate, and advanced, up to best-in-class (fully optimized systems). Each tier reflects a band of total scoring rather than a single metric.', where: 'Shown on the Scorecard tab' },
        { term: 'Weighted scoring', def: 'Weighted scoring is a method of calculating an overall score in which different components contribute unequally based on their importance. Categories deemed more critical receive higher weights, so their performance influences the final number more than less critical ones. The result reflects strategic priority rather than treating all inputs as equal.', where: 'Drives your overall maturity score' },
        { term: 'Effective status', def: 'Effective status refers to the final state of a checklist item after contextual rules have been applied. Some items may be automatically reclassified — for example, marked as not applicable — based on a user\'s circumstances. The effective status is what the audit actually counts during scoring.', where: 'How items are treated in scoring' },
        { term: 'Five Moments', def: 'The Five Moments framework groups personalization activity by stages of the customer journey rather than by feature category. The five moments are: First visit, Discovery, Cart, Post-purchase, and Re-engagement. Organizing checkpoints this way reveals gaps that exist across the customer experience over time, which a category-based view can obscure.', where: 'Second view on the Scorecard tab' },
        { term: 'Data state', def: 'Data state describes the level of customer data a business has collected and can act on. State 1 means only anonymous traffic; State 2 means purchase history exists for known customers; State 3 means behavioral data and stated preferences are also captured. Each state unlocks different personalization tactics, since you can only personalize on the data you have.', where: 'Section 1 of Context, callout in Verdict' },
        { term: 'Backlog by horizon', def: 'Backlog by horizon organizes pending tasks by how soon they can realistically be completed, rather than by priority alone. Common horizons include "today" (quick fixes), "this week" (small projects requiring some configuration), and "this quarter" (larger initiatives requiring real effort). This grouping helps teams sequence work to match available capacity.', where: 'Verdict tab, section III' },
      ]
    },
    {
      section: 'Personalization data types',
      terms: [
        { term: 'Zero-party data', def: 'Zero-party data is information that customers intentionally and proactively share with a business, such as quiz answers, preference selections, or stated interests. Because customers volunteer it directly, it is considered the most accurate type of customer data, with no inference required. Businesses typically collect it through forms, quizzes, preference centers, or post-purchase questions.', where: 'Checkpoint 0.2, 3.12' },
        { term: 'First-party data', def: 'First-party data is information a business collects directly from its own interactions with customers. This includes purchase history, account profiles, browsing behavior, email engagement, and app usage. It is owned by the business that collects it and does not depend on third-party tracking.', where: 'Checkpoint 9.5' },
        { term: 'Third-party data', def: 'Third-party data is information collected by an entity that has no direct relationship with the consumer, then sold or shared with other businesses. It is typically aggregated by data brokers from many sources. Its availability and reliability have declined sharply due to browser privacy changes, mobile OS restrictions, and privacy regulations.', where: 'Mentioned as foundation context' },
        { term: 'CDP', alias: 'Customer Data Platform', def: 'A Customer Data Platform is software that consolidates customer data from multiple sources — website, email, mobile app, in-store — into a single unified profile per person. Unlike a traditional database, a CDP ingests data from many systems automatically and makes it available to other tools in real time. CDPs commonly enable cross-channel personalization and identity resolution.', where: 'Checkpoint 1.9, 9.4; Tech stack section' },
        { term: 'ESP', alias: 'Email Service Provider', def: 'An Email Service Provider is a software platform businesses use to send marketing and transactional emails at scale. ESPs typically handle list management, segmentation, automated flows, deliverability monitoring, and reporting. Most also include templating, A/B testing, and integration with ecommerce or CRM systems.', where: 'Tech stack section, all of Category 5' },
        { term: 'Identity stitching', alias: 'Identity resolution', def: 'Identity stitching is the process of linking activity across different devices, channels, or sessions to a single customer record. For example, recognizing that someone who browsed on a phone, opened an email on a laptop, and purchased on a tablet is one person. It typically relies on shared identifiers such as email address or unified IDs.', where: 'Checkpoint 1.9, 9.4' },
        { term: 'Server-side tracking', def: 'Server-side tracking is the practice of sending analytics and conversion events from a website\'s backend server, instead of from the visitor\'s browser. Because the events originate on the server, they are not blocked by ad blockers, browser privacy features, or operating-system tracking restrictions. It is considered a more reliable method of capturing event data.', where: 'Checkpoint 9.1' },
        { term: 'Conversions API', alias: 'CAPI', def: 'The Conversions API is a server-side endpoint provided by advertising platforms — most notably Meta, with similar systems for TikTok and Google — that lets businesses send conversion events directly from their server to the ad platform. It is designed to complement or replace the browser-side pixel, which is increasingly limited by privacy controls.', where: 'Checkpoint 9.1' },
      ]
    },
    {
      section: 'Funnel & behaviour metrics',
      terms: [
        { term: 'Conversion rate', def: 'Conversion rate is the percentage of visitors to a website who complete a specific desired action, most commonly a purchase. It is calculated by dividing the number of conversions by the number of sessions over a given time period. Definitions vary by platform, so comparisons across tools need to be made carefully.', where: 'Performance metric in Context section' },
        { term: 'Sessions', def: 'A session is a single visit to a website, typically measured from the first page view until a period of inactivity (commonly 30 minutes) ends it. One user can have many sessions over time. Sessions are different from users (people) and pageviews (individual page loads).', where: 'Performance metric in Context section' },
        { term: 'Engaged sessions', def: 'An engaged session is a visit that meets a minimum threshold of meaningful interaction. In Google Analytics 4, a session counts as engaged if it lasts at least 10 seconds, includes a conversion event, or has at least two pageviews. The metric filters out accidental visits and reflects genuine engagement.', where: 'Glossary reference' },
        { term: 'AOV', alias: 'Average Order Value', def: 'Average Order Value is the average revenue generated per order over a given time period, calculated by dividing total revenue by the number of orders. It is a core ecommerce metric that reflects how much customers typically spend in a single transaction. AOV is often used alongside conversion rate to assess store performance.', where: 'Section 4 of Context' },
        { term: 'LTV', alias: 'Customer Lifetime Value', def: 'Customer Lifetime Value is the total revenue a customer generates for a business over the entire duration of their relationship. It can be measured retrospectively from purchase history or projected forward based on expected behavior. LTV commonly informs how much a business can spend acquiring or retaining a customer.', where: 'Used to calibrate win-back incentives (5.14)' },
        { term: 'Repeat purchase rate', def: 'Repeat purchase rate is the percentage of customers who place more than one order within a defined time window, typically 12 months. It is a common indicator of customer retention and product-market fit. The metric is sometimes called returning customer rate.', where: 'Performance metric in Context section' },
        { term: 'Cart abandonment rate', def: 'Cart abandonment rate is the percentage of online shopping carts created but not completed with a purchase. It is calculated as the number of abandoned carts divided by total carts started. Rates vary by industry but typically fall between 60% and 80%.', where: 'Performance metric in Context section' },
        { term: 'Checkout abandonment rate', def: 'Checkout abandonment rate is the percentage of shoppers who begin the checkout process — entering shipping or payment information — but leave before completing the purchase. It is narrower than cart abandonment, focusing only on shoppers who showed clear intent to buy. The two metrics are often analyzed together to diagnose funnel friction.', where: 'Glossary reference' },
        { term: 'Email contribution to revenue', def: 'Email contribution to revenue is the percentage of total store revenue attributed to the email marketing channel. It is calculated by dividing email-attributed revenue by total revenue over the same period. The exact figure depends on the attribution model used, since email may be one of several touchpoints in a path to purchase.', where: 'Performance metric in Context section' },
      ]
    },
    {
      section: 'Channels & flows',
      terms: [
        { term: 'Welcome series', def: 'A welcome series is an automated sequence of messages sent to a new subscriber or customer shortly after they join a marketing list. It typically introduces the brand, sets expectations for future communication, and may include an incentive such as a discount. The sequence usually runs across several messages over a few days or weeks.', where: 'Checkpoint 5.1' },
        { term: 'Browse abandonment', def: 'Browse abandonment is an automated message — usually email or SMS — triggered when an identified visitor views one or more product pages without adding anything to their cart. It reminds the shopper of products they showed interest in. It sits earlier in the funnel than cart abandonment, since the customer never reached the cart.', where: 'Checkpoint 5.2' },
        { term: 'Cart abandonment', def: 'A cart abandonment flow is an automated sequence triggered when a customer adds items to their cart but does not complete checkout. The sequence typically includes one or more reminder messages, sometimes with incentives such as free shipping or a discount. Multi-message flows generally recover more sales than a single follow-up.', where: 'Checkpoint 4.8, 5.3, 6.3' },
        { term: 'Win-back flow', def: 'A win-back flow is an automated sequence aimed at re-engaging customers who have not purchased in a defined period. It typically includes a brand reminder, social proof or new product news, and sometimes a comeback incentive. The trigger window is usually based on the brand\'s typical repurchase cycle.', where: 'Checkpoint 5.5, 5.13, 5.14' },
        { term: 'Send-time optimization', alias: 'STO', def: 'Send-time optimization is a feature in modern email and SMS platforms that schedules messages to be delivered when each individual recipient is most likely to engage. The platform learns from past open and click behavior to predict the optimal delivery moment. Different recipients in the same campaign may receive the message at different times.', where: 'Checkpoint 5.10' },
        { term: 'Replenishment reminder', def: 'A replenishment reminder is an automated message sent to a customer some time after they purchased a consumable product, prompting reorder before they run out. The timing is calibrated to the typical usage cycle of the product. It is often used as an alternative to subscription programs for customers who prefer not to commit to recurring orders.', where: 'Checkpoint 5.4, 8.4' },
        { term: 'Frequency cap', def: 'A frequency cap is a rule that limits how many messages a single recipient can receive from a brand within a defined time window — for example, a maximum of four emails per week. It applies across all flows and campaigns rather than to any single message. The purpose is to protect deliverability and reduce subscriber fatigue.', where: 'Checkpoint 5.12' },
      ]
    },
    {
      section: 'Compliance & privacy',
      terms: [
        { term: 'GDPR', alias: 'General Data Protection Regulation', def: 'The General Data Protection Regulation is a data protection and privacy law that applies to people in the European Union and the United Kingdom. It requires businesses processing personal data to obtain a lawful basis (typically explicit consent), to be transparent about data practices, and to honor rights such as access, correction, and deletion. Penalties can reach 4% of a company\'s global annual revenue.', where: 'Checkpoint 10.1, 10.5; Markets section' },
        { term: 'CCPA', alias: 'California Consumer Privacy Act', def: 'The California Consumer Privacy Act is a state-level privacy law that gives California residents specific rights over how their personal information is collected, used, and shared. Rights include access, deletion, correction, and the ability to opt out of the sale or sharing of personal information. The CCPA, as amended by CPRA, applies to businesses meeting certain revenue or data-volume thresholds.', where: 'Checkpoint 10.6; Markets section' },
        { term: 'TCPA', alias: 'Telephone Consumer Protection Act', def: 'The Telephone Consumer Protection Act is a US federal law that regulates telemarketing calls, automated calls, prerecorded messages, and SMS marketing. It requires businesses to obtain prior express written consent before sending marketing messages to a phone number. Violations can result in statutory damages of $500 to $1,500 per message.', where: 'Checkpoint 6.6, 10.7' },
        { term: 'Double opt-in', def: 'Double opt-in is a subscription confirmation process that requires a user to verify their signup through a second action — typically clicking a confirmation link or replying to a confirmation text. Only after this second confirmation is the contact added to the active list. The practice reduces fake or accidental signups and is required for SMS marketing in some jurisdictions.', where: 'Checkpoint 10.7' },
        { term: 'DSAR', alias: 'Data Subject Access Request', def: 'A Data Subject Access Request is a formal request from an individual asking a business to disclose what personal data it holds about them and how that data is used. Some privacy laws also let individuals request correction, deletion, or transfer of that data through the same process. Regulations such as GDPR and CCPA set specific timelines for response.', where: 'Checkpoint 10.8' },
        { term: 'Cookie consent banner', def: 'A cookie consent banner is a notice displayed to first-time visitors, informing them about the use of cookies and tracking technologies and asking for consent. To comply with privacy laws in many jurisdictions, the banner must categorize cookies by purpose, allow users to reject non-essential cookies as easily as accepting them, and respect the choice across the visit.', where: 'Checkpoint 10.1' },
      ]
    },
    {
      section: 'Customer types',
      terms: [
        { term: 'B2C', alias: 'Business-to-Consumer', def: 'B2C refers to commerce conducted between a business and individual end consumers, in which a company sells products or services directly to people for personal use. It is the dominant model in retail and most online shopping. B2C transactions are typically smaller and more frequent than B2B transactions, and purchasing decisions are often driven by individual preferences and emotions.', where: 'Section 2 of Context' },
        { term: 'B2B', alias: 'Business-to-Business', def: 'B2B refers to commerce conducted between companies, rather than between companies and individual consumers. It involves one business selling products, services, or raw materials to another, often focused on efficiency, operations, or wholesale supply. B2B relationships typically involve longer sales cycles, negotiated pricing, and recurring orders.', where: 'Section 2 of Context; Checkpoint 1.7' },
        { term: 'Subscription', def: 'A subscription is a commercial model in which a customer agrees to receive a product or service on a recurring basis, typically with automatic billing and delivery at fixed intervals. It is used across media, software, food, and consumer goods. Subscription models prioritize customer retention and predictable recurring revenue over one-time transactions.', where: 'Section 2 of Context' },
        { term: 'VIP customer', def: 'A VIP customer is a high-value customer identified by a business as deserving differentiated treatment, typically based on lifetime spend, order frequency, or another loyalty signal. The designation is usually applied to the top 10% or so of the customer base. Businesses often give VIP customers perks such as early access, exclusive offers, or dedicated support.', where: 'Checkpoint 1.6, 5.6, 8.1, 8.2' },
        { term: 'Lapsed customer', def: 'A lapsed customer is a previous purchaser who has not made another purchase within an expected time frame, suggesting they may be drifting away. The exact threshold varies by business and product type, since a 90-day gap means something different for coffee than for furniture. Lapsed customers are a common target for re-engagement campaigns.', where: 'Checkpoint 5.5, 5.13, 5.14' },
        { term: 'Spend tier', def: 'A spend tier is a customer segment defined by the cumulative amount a customer has spent over a given time period. Tiers are typically ordered from low-spend to high-spend bands, with each representing a different value bracket. Spend tiers are often used to tailor marketing offers, loyalty access, or retention strategies.', where: 'Checkpoint 1.6' },
      ]
    },
    {
      section: 'Shopify-specific',
      terms: [
        { term: 'Shop Pay', def: 'Shop Pay is Shopify\'s accelerated checkout feature, which saves a shopper\'s contact, shipping, and payment information for one-tap reuse across any Shopify store. When a returning shopper is recognized, their details auto-populate so they can complete a purchase without re-entering information. It is available on Shopify stores using Shopify Payments.', where: 'Checkpoint 4.7, 4.9' },
        { term: 'Shopify Plus', def: 'Shopify Plus is Shopify\'s enterprise-tier subscription, designed for high-volume merchants and brands with complex operational requirements. It includes features such as customizable checkout, B2B commerce tools, expansion stores, dedicated automation, and a higher uptime guarantee than standard plans. Pricing is tiered by contract length and adjusts based on revenue volume.', where: 'Section 2 (some checkpoints are Plus-only)' },
        { term: 'Customer Accounts (Classic)', def: 'Customer Accounts (Classic) is Shopify\'s original customer login system, in which shoppers create an account with an email and password specific to that store. Account holders can save shipping addresses, view order history, and manage profile details. It is being phased out in favor of New Customer Accounts.', where: 'Checkpoint 1.5' },
        { term: 'New Customer Accounts', def: 'New Customer Accounts is Shopify\'s modernized customer account system, which uses passwordless email-based authentication instead of a traditional email-and-password login. Customers receive a one-time code by email each time they sign in. The system reduces login friction and improves account adoption rates.', where: 'Checkpoint 1.5' },
        { term: 'Shopify Bundles', def: 'Shopify Bundles is a native Shopify feature that lets merchants group multiple products together as a single purchasable bundle, often at a discount. Inventory is tracked at the individual product level, so each component\'s stock decreases as bundles are sold. The feature is built into Shopify and does not require a third-party app.', where: 'Checkpoint 4.4' },
        { term: 'Shopify Markets', def: 'Shopify Markets is a built-in feature for selling to multiple regions or countries from a single Shopify store. It supports localized currency, language, domain, payment methods, and shipping rules per market. It is the foundation for cross-border ecommerce on the Shopify platform.', where: 'Checkpoint 1.3, 4.6' },
        { term: 'Customer tags', def: 'Customer tags are user-defined labels attached to customer records in Shopify to categorize or segment them. Tags can be applied manually, through automation rules, or by connected third-party apps. They are commonly used to drive segmentation in marketing tools, restrict access to specific products or pricing, or trigger workflows.', where: 'Checkpoint 1.6, 9.7' },
      ]
    },
  ];

  // ========== INPUT DEFINITIONS ==========
  const DATA_STATES = [
    { id: 'state1', num: 'State 01', name: 'You have nothing yet', desc: 'Anonymous traffic, no purchase history, no email list.' },
    { id: 'state2', num: 'State 02', name: 'You have purchase history', desc: 'Customers have bought once or more. Email + order data exist.' },
    { id: 'state3', num: 'State 03', name: 'Behaviour & stated preferences', desc: 'Quiz answers, preference centre, browse history all in play.' },
    { id: 'skip',   num: 'Not sure', name: 'I\'m not sure', desc: 'We\'ll infer your state from your checkpoint pattern instead.' },
  ];

  const CUSTOMER_MODELS = [
    { id: 'b2c', name: 'B2C', desc: 'Direct to consumer. Most Shopify stores live here.' },
    { id: 'b2b', name: 'B2B', desc: 'Selling to businesses. Account-based, wholesale, bulk.' },
    { id: 'sub', name: 'Subscription', desc: 'Recurring orders are a major part of revenue.' },
    { id: 'mix', name: 'Mixed B2C + B2B', desc: 'You sell to both consumers and businesses.' },
    { id: 'mix-sub', name: 'B2C + Subscription', desc: 'One-off and subscription customers in parallel.' },
    { id: 'skip', name: 'Not sure', desc: 'Default B2C weighting will apply.' },
  ];

  const MARKETS = [
    { id: 'eu', name: 'Sell to EU / UK customers', hint: 'Triggers GDPR-compliance checkpoints' },
    { id: 'ca', name: 'Sell to California customers', hint: 'Triggers CCPA-compliance checkpoint' },
    { id: 'multi', name: 'Sell across multiple countries', hint: 'Triggers geo-detection checkpoint' },
    { id: 'other_intl', name: 'Sell to other international markets (not EU/UK or California)', hint: 'e.g. Canada, Australia, LATAM, Asia — keeps geo-detection relevant without GDPR/CCPA flags' },
  ];

  const AOV_BRACKETS = [
    { id: 'low', name: 'Under $50', desc: 'Basket-builders matter most.' },
    { id: 'mid', name: '$50 – $200', desc: 'Standard ecommerce range.' },
    { id: 'high', name: 'Over $200', desc: 'Every order matters; cart items get extra weight.' },
    { id: 'skip', name: 'Not sure', desc: 'Default Impact scores will apply.' },
  ];

  const STACK_TOOLS = [
    { id: 'esp', name: 'Email service provider', hint: 'Klaviyo, Omnisend, Mailchimp, Shopify Email', suggests: ['5.1', '5.3', '5.4'] },
    { id: 'sms', name: 'SMS marketing tool', hint: 'Postscript, Attentive, Klaviyo SMS', suggests: ['6.1', '6.2', '6.3'] },
    { id: 'loyalty', name: 'Loyalty / rewards platform', hint: 'Smile.io, Koin, Yotpo Loyalty', suggests: ['8.1', '8.2', '8.3'] },
    { id: 'quiz', name: 'Quiz / zero-party data tool', hint: 'Octane AI, RevenueHunt, Typeform', suggests: ['3.12', '0.2'] },
    { id: 'cdp', name: 'Customer Data Platform (CDP)', hint: 'Segment, mParticle, Klaviyo CDP', suggests: ['1.9', '1.10', '9.4'] },
    { id: 'recs', name: 'Personalization / recs engine', hint: 'Rebuy, LimeSpot, Glood', suggests: ['3.1', '3.2', '3.4'] },
  ];

  const PERF_METRICS = [
    {
      id: 'cr_overall',
      name: 'Overall conversion rate',
      window: 'Last 90 days',
      hint: 'Total orders ÷ total sessions, all devices. <strong>Where to find it:</strong> Shopify Analytics → Online Store Conversion Rate, or GA4 → Engagement → Conversions.',
      placeholder: 'e.g. 2.4',
      suffix: '%',
      effect: 'Below 1.5% — your verdict will focus on getting the basics right before layering advanced personalization.',
    },
    {
      id: 'cr_mobile',
      name: 'Mobile conversion rate',
      window: 'Last 90 days',
      hint: 'Mobile sessions only, same formula. Compare to desktop. <strong>Where to find it:</strong> Shopify Analytics → filter by device, or GA4 → device breakdown.',
      placeholder: 'e.g. 1.6',
      suffix: '%',
      effect: 'Significantly lower than desktop — mobile UX checkpoints will be flagged as higher priority.',
    },
    {
      id: 'repeat_rate',
      name: 'Repeat purchase rate',
      window: 'Last 12 months',
      hint: '% of customers who placed 2+ orders. <strong>Where to find it:</strong> Shopify Analytics → Customer reports → Returning customer rate.',
      placeholder: 'e.g. 24',
      suffix: '%',
      effect: 'Below 20% — retention and win-back items will be pushed up your priority list.',
    },
    {
      id: 'cart_abandon',
      name: 'Cart abandonment rate',
      window: 'Last 90 days',
      hint: '% of carts started but not completed. <strong>Where to find it:</strong> Shopify Analytics → Behavior → Online store conversion funnel.',
      placeholder: 'e.g. 72',
      suffix: '%',
      effect: 'Above 70% — cart and checkout fixes will rank higher in your gap log.',
    },
    {
      id: 'email_pct',
      name: 'Email contribution to revenue',
      window: 'Last 90 days',
      hint: '% of total revenue attributed to email. <strong>Where to find it:</strong> Klaviyo dashboard → Total revenue, divide by Shopify total revenue.',
      placeholder: 'e.g. 18',
      suffix: '%',
      effect: 'Below 15% — email program improvements will be weighted higher.',
    },
  ];

  // ========== CATEGORIES — titles are imperative actions; descriptions explain what & why ==========
  const CATEGORIES = [
    { id: 0, weight: 1.5, title: 'Data foundation for personalization', isFoundation: true,
      blurb: `Personalization runs on data you've actually captured. Most merchants try tactics meant for data they don't have — and wonder why nothing lifts.`,
      items: [
        ['0.1', `Remove personalization tools your data can't power`,
          `A predictive recs engine needs purchase history to learn from. A multi-segment ESP needs customer profiles to segment. Tools that need data you haven't captured aren't personalizing — they're guessing. Audit your stack: uninstall what doesn't fit, or capture the data first.`, 'foundation'],
        ['0.2', `Ask customers questions that help you personalize`,
          `Ask shoppers what they want, who they're shopping for, or what fits them — at signup, in a quiz, or after purchase. One answer they give you directly beats ten you have to guess. Store the answer on the customer record so every tool can use it.`, 'foundation'],
        ['0.3', `Make sure your tools recognize the same person as one customer`,
          `When a shopper visits on mobile, opens an email, and buys on desktop, your tools should know it's one person — not three. Pick how you'll identify customers (usually by email) and check that every tool you use follows the same rule. Otherwise the same person gets treated like a stranger every time.`, 'foundation'],
      ]},
    { id: 1, weight: 1.5, title: 'Visitor identification & segmentation',
      blurb: `Foundation layer. Without good identification, all downstream personalization breaks.`,
      items: [
        ['1.1', `Recognize new vs. returning visitors`,
          `Use cookies or browser storage to know whether someone has been to your store before. Once you can tell them apart, new visitors can see a first-time offer while returning visitors see something that acknowledges they've been here. Showing the same homepage to both wastes the difference.`, 'first'],
        ['1.2', `Treat logged-in customers differently from guest visitors`,
          `A signed-in customer should get pre-filled addresses, account-specific pricing, "your usual" reorder shortcuts, or their loyalty status. If logged-in and guest experiences look identical, the trust signal of being signed in is wasted.`, 'first'],
        ['1.3', `Adapt currency, language, and shipping to the visitor's country`,
          `A visitor from Germany should land on EUR pricing in German with shipping ETAs to Berlin — not "USD with Free shipping in the US". Detect their location automatically (with an option to override) so they don't have to mentally convert anything.`, 'first'],
        ['1.4', `Adapt the experience by device, not just the layout`,
          `Mobile shoppers need thumb-friendly CTAs, simplified navigation, and a different content hierarchy than desktop users. "Responsive" themes that just shrink the desktop layout for phones miss the point — mobile is a different shopping context, not a smaller screen.`, 'first'],
        ['1.5', `Enable customer accounts as an option (not a requirement)`,
          `Turn on Shopify Customer Accounts so returning customers can save addresses, see order history, and check out faster. Keep it optional — forcing account creation before purchase reduces conversions. The goal is to make accounts attractive enough that returning customers want one, not to require them.`, 'foundation'],
        ['1.6', `Auto-tag customers when they cross meaningful thresholds`,
          `Set automatic rules to tag customers as first-time-buyer, VIP (top 10% spenders), lapsed (no order in 90 days), or bulk-buyer when they cross those thresholds. Tags drive segments and flows downstream — and the work happens once, automatically, instead of manually.`, 'foundation'],
        ['1.7', `Personalize within your B2B segment, not just between B2B and B2C`,
          `A wholesale buyer reordering monthly needs different reorder shortcuts, pricing, and rep visibility than a new prospect filling a first PO. Treating all B2B customers as one segment leaves money on the table.`, 'foundation'],
        ['1.8', `Use traffic source to vary what the visitor sees`,
          `A visitor arriving from a Facebook ad shouldn't see the same homepage as one arriving from your newsletter or a Google search. Capture the source (UTM parameters, referrer) and use it to vary at least one element — hero banner, popup offer, or featured collection. Same homepage for every source wastes the most-viewed pixel real estate on your site.`, 'first'],
        ['1.9', `Recognize the same person across devices`,
          `A customer who browses on their phone, opens your email on a laptop, and buys on a tablet should be one customer in your system — not three. Identity stitching usually comes from a CDP, email-based login, or Shop Pay's identity graph.`, 'foundation'],
        ['1.10', `Keep customer data in sync across your tools`,
          `The same customer should look the same in Shopify, your ESP, and your analytics — same tags, same order history, same segment membership. When data drifts (VIP in one tool, first-time buyer in another), every personalization tactic downstream contradicts itself.`, 'foundation'],
        ['1.11', `Personalize for anonymous visitors, not just identified ones`,
          `Most of your traffic has no account and no purchase history. Use what you do have — geo, device, time of day, traffic source, session behaviour — to vary popups, hero banners, and product order. Treating all anonymous traffic the same wastes the majority of your sessions.`, 'first'],
      ]},
    { id: 2, weight: 1.0, title: 'Homepage & landing page personalization',
      blurb: `The first page someone sees is your most valuable real estate. Showing the same one to everyone wastes it.`,
      items: [
        ['2.1', `Vary the hero banner by traffic source`,
          `A visitor arriving from a TikTok ad should see a hero that matches that ad's product and message. A newsletter visitor sees something different again. Showing the same static hero to everyone wastes the most-viewed pixel real estate on your site.`, 'first'],
        ['2.2', `Adapt the hero and featured collections by geo`,
          `Lead with winter coats for Canadian visitors in November and swimwear for Australian ones at the same moment. Currency, product mix, and seasonal messaging should follow the visitor's location, not your headquarters' season.`, 'first'],
        ['2.3', `Show returning visitors something that picks up where they left off`,
          `A returning shopper should see a "Continue where you left off" row, their saved cart, or products curated from what they browsed last time. A simple "Welcome back" isn't enough — recognition only matters if the page changes because of it.`, 'first'],
        ['2.4', `Surface recently viewed products on the homepage`,
          `When someone returns within 30 days, give them a dedicated row of "Pick up where you left off" with the products they previously looked at. Saves them re-finding things and signals that you remember them.`, 'discovery'],
        ['2.5', `Reorder featured collections by browse / purchase history`,
          `A customer who has bought running shoes should see the running collection above the climbing collection on their next visit, even if you generally lead with climbing. The store should learn from each customer and rearrange accordingly.`, 'discovery'],
        ['2.6', `Personalize the sort order on category pages`,
          `On a category page, products should be sorted by what this specific customer is most likely to buy — based on past purchases, dwell time, and category affinity — instead of a single "Featured" or "Best Selling" order applied to everyone.`, 'discovery'],
        ['2.7', `Match paid ad landing pages to the ad creative`,
          `When the ad says "30% off all denim", the landing page should lead with denim and the discount — not the generic homepage. Mismatch between ad creative and landing page is one of the most common reasons paid traffic bounces.`, 'first'],
        ['2.8', `Surface account-specific content above the fold for logged-in customers`,
          `Wholesale customers should see their wholesale prices on the homepage, loyalty members should see member-only collections, subscribers should see their subscription status — all above the fold, without navigating to a hidden page. Logging in shouldn't feel the same as not logging in.`, 'discovery'],
        ['2.9', `Time the welcome popup to genuine first-time visitors only`,
          `The "10% off your first order" popup should fire only for true first-time visitors — not on every session of every returning customer. Use cookies or visit count to gate it. Returning customers seeing the same first-time popup over and over signals you don't recognize them.`, 'first'],
      ]},
    { id: 3, weight: 1.0, title: 'Product discovery & search',
      blurb: `Recommendations and search are the two highest-traffic surfaces in your store. Defaults from your theme leave most of their value untapped.`,
      items: [
        ['3.1', `Show "You might also like" on every product page`,
          `Add a row of similar products below the buy box on every PDP. Gives shoppers an alternative if the current product isn't quite right and prevents them from leaving to compare elsewhere.`, 'discovery'],
        ['3.2', `Add "Frequently bought together" to the PDP`,
          `Surface 1–3 items historically purchased alongside the current product (e.g. surfboard + leash + wax). Drives bigger basket sizes when the relevance is good — but only useful if your bundling logic is real, not random.`, 'discovery'],
        ['3.3', `Display a "Recently viewed" carousel`,
          `A persistent strip showing the last 5–10 products this visitor looked at, available on PDPs and category pages. Saves them clicking back through to compare options and reduces cognitive load.`, 'discovery'],
        ['3.4', `Cross-sell complements based on cart contents`,
          `When a shopper opens their cart, show 2–4 items that complete what they're already buying — accessories, batteries, refills, the matching belt. Last chance to add value before checkout, but the suggestions must be tied to what's in the cart, not generic.`, 'cart'],
        ['3.5', `Show recommendations on the order confirmation page`,
          `After purchase, surface "Customers also bought" or "Complete the set" while the credit card is still warm. Catches impulse follow-up purchases that would otherwise wait until your next email.`, 'post'],
        ['3.6', `Re-rank search results by user behaviour`,
          `When customer A searches "shoes" and has browsed running gear, the top result should be running shoes. Customer B with no history sees boots first. Same query, personalized ranking.`, 'discovery'],
        ['3.7', `Personalize search auto-suggestions`,
          `As the customer types, suggest their recent searches and categories they care about — not just generic top sellers. "leg…" might autocomplete to "leggings" for one user and "lego" for another.`, 'discovery'],
        ['3.8', `Offer fallback recommendations on empty search results`,
          `When a search returns zero results, don't end the conversation with "No results". Show "We don't have that, but you might like…" with an algorithmic fallback. Prevents the bounce that empty states cause.`, 'discovery'],
        ['3.9', `Persist filter preferences across visits`,
          `Save filter selections (size M, vegan, under $50) so they apply automatically next visit, or based on past behaviour. Customers shouldn't re-select the same five filters every time.`, 'discovery'],
        ['3.10', `Use a different recommendation algorithm in each placement`,
          `PDP should use "similar to this." Cart should use "completes the basket." Email should use "predicted next purchase." If one algorithm runs every slot, you'll see the same products on the PDP, cart, and email — and customers notice.`, 'discovery'],
        ['3.11', `Filter already-purchased and out-of-stock items from every recommendation`,
          `A customer who bought the navy hoodie last week should never see it in their recommendations again. Out-of-stock items should be filtered from every recommendation slot. Most recommendation engines do this by default — but only if you've enabled the toggle.`, 'discovery'],
        ['3.12', `Collect zero-party data through quizzes or preference centres`,
          `Add a quiz, preference centre, or post-purchase question that captures what customers tell you directly — their skin type, fitness level, gift recipient. Even one tag, applied consistently, dramatically improves every email and recommendation that follows.`, 'discovery'],
      ]},
    { id: 4, weight: 1.5, title: 'Cart & checkout personalization',
      blurb: `Cart and checkout is where personalization either pays back or breaks. Small lifts here compound on every order.`,
      items: [
        ['4.1', `Upsell to a higher-value alternative based on cart contents`,
          `If the cart has the standard model, suggest the premium version. If it has a single product, suggest the bundle. Upsells move customers up the value ladder — distinct from cross-sells (covered in 3.4), which add complementary items. Most stores conflate the two and miss the upsell entirely.`, 'cart'],
        ['4.2', `Show free-shipping threshold progress in the cart`,
          `Display a bar that says "Add $12 more for free shipping" with a visual progress indicator that updates live as items change. Reliably lifts AOV by 5–10% on most stores — one of the easiest wins on this list.`, 'cart'],
        ['4.3', `Escalate threshold tiers for higher carts`,
          `After hitting free shipping at $50, introduce a second tier: "Add $30 more for a free gift". Then a third: "Add $50 more for 15% off". Stacked thresholds extract more spend from already-motivated buyers.`, 'cart'],
        ['4.4', `Offer bundle pricing in the cart drawer`,
          `When 2 of 3 items in a known bundle are in the cart, prompt: "Add the third and save 10%". Smart, contextual bundling beats blanket sitewide discounts because it rewards customers already on the path.`, 'cart'],
        ['4.5', `Tailor checkout trust signals to the visitor`,
          `Show more trust badges (reviews, BBB, money-back guarantee) to first-time visitors from paid ads than to logged-in repeat customers. International customers should see local payment marks. Don't give away credibility you've already earned.`, 'cart'],
        ['4.6', `Adapt payment methods to the visitor's region`,
          `Ideal for Dutch customers, Klarna for Germans, Afterpay for Australians, Shop Pay everywhere. Default Shopify already does some of this — verify it's actually configured for your markets, not just available.`, 'cart'],
        ['4.7', `Vary checkout trust messaging by cart value`,
          `A $30 impulse buy and a $300 considered purchase need different reassurance. Low-cart visitors need clarity on return policy and shipping speed; high-cart visitors need security badges, warranty info, and proof of fit. One trust block for every cart value wastes the signal.`, 'cart'],
        ['4.8', `Sequence cart abandonment recovery across channels`,
          `Build a sequence: email at 1hr, second email at 24hr, SMS at 48hr (if opted in), retargeting ad on day 3. The orchestration matters more than any single channel — single-email recovery captures roughly half what a sequenced flow does.`, 'cart'],
        ['4.9', `Pre-fill checkout for returning customers`,
          `Use Shop Pay or Shopify Customer Accounts to auto-fill name, address, and saved cards for repeat customers. The goal: returning customers should be one tap from "Place order", not 12 form fields away.`, 'cart'],
        ['4.10', `Adapt the post-purchase upsell offer to what they just bought`,
          `The page after "Place Order" can show a one-click-add offer — but the offer must connect to the purchase. Buying a coffee machine? Offer beans or a warranty. Buying a single item? Offer the matching accessory at a discount. Generic post-purchase upsells get ignored; relevant ones convert at high rates.`, 'cart'],
        ['4.11', `Calibrate post-purchase incentives to customer value`,
          `A first-time buyer's thank-you page should offer something different than a fifth-time VIP's. New customers might see a "second order, 15% off" coupon to drive the repeat purchase. Existing high-value customers might see early access to a new product. Same thank-you page for every customer wastes the trust peak.`, 'post'],
      ]},
    { id: 5, weight: 1.0, title: 'Email personalization',
      blurb: `Email is the highest-margin channel for most stores. Personalization here usually returns the strongest ROI of any tactic in this audit.`,
      items: [
        ['5.1', `Segment your welcome series by signup source`,
          `Someone who signed up via popup has different intent than someone who opted in at checkout. Send each group a tailored welcome flow. Same series for both wastes the highest-engagement moment in the customer relationship.`, 'first'],
        ['5.2', `Set up a browse abandonment flow`,
          `A visitor browses 3 PDPs but doesn't add to cart. Four hours later, send them an email featuring those exact products. Earlier-funnel cousin of cart abandonment, often un-implemented because it requires identified browsing.`, 'discovery'],
        ['5.3', `Run a multi-email cart abandonment sequence`,
          `Three or more touchpoints in the email leg alone: gentle reminder at 1hr, social proof or urgency at 24hr, last-chance discount at 72hr. The orchestration across email, SMS, and ads sits in checkpoint 4.8 — this one is about the email sequence specifically.`, 'cart'],
        ['5.4', `Build a complete post-purchase flow`,
          `Sequence after delivery: thank-you, review request 5 days later, complementary product cross-sell at 14 days, replenishment reminder at 60 days for consumables. Most stores send a confirmation and go silent — that's a missed retention moment.`, 'post'],
        ['5.5', `Trigger a win-back flow for lapsed customers`,
          `When a customer hasn't bought in 90/120/180 days, send them a re-engagement sequence — possibly with an exclusive comeback offer. Your existing list is more valuable than any cold acquisition channel.`, 'reengage'],
        ['5.6', `Recognize VIP / loyalty customers in email`,
          `Top-tier customers should get early access, exclusive products, or a "thank you for being a VIP" treatment. Acknowledging spend creates loyalty; ignoring it makes top customers feel like every other one.`, 'post'],
        ['5.7', `Send birthday or anniversary emails`,
          `A small gesture — 15% off on their birthday, a free gift on the anniversary of their first order. Cheap to set up, gets high open rates, builds the emotional connection that pure transactional emails never do.`, 'post'],
        ['5.8', `Make in-email product blocks dynamic per recipient`,
          `Each recipient should see different products in the email body based on their past behaviour. Klaviyo and most modern ESPs do this natively. Static product blocks across all sends to all subscribers is just laziness.`, 'post'],
        ['5.9', `Use first names and purchase context sparingly`,
          `Reference the customer by first name in subject lines, and reference their last order in body copy ("Restock your favourite"). But use both sparingly — overuse is creepy, can trip spam filters, and the lift comes from relevance, not just inserting a name.`, 'post'],
        ['5.10', `Enable send-time optimization`,
          `Each recipient gets emails at the time they're historically most likely to open. Klaviyo, Omnisend, and most modern ESPs have this built in — many merchants just leave it off because they don't know it exists.`, 'post'],
        ['5.11', `Vary email content blocks by recipient segment`,
          `In a single campaign, different segments should see different lead products and copy — high-spenders see premium SKUs first, region-specific subscribers see local stock, repeat buyers see new arrivals while first-timers see best-sellers. Granular content blocks within one campaign multiply the relevance of every send.`, 'post'],
        ['5.12', `Cap how many emails any one subscriber can receive in a week`,
          `Add a global rule that no recipient receives more than X emails in a 7-day window, regardless of how many flows trigger them. Protects deliverability and prevents your most-engaged customers from burning out.`, 'post'],
        ['5.13', `Time win-backs to your repurchase cycle, not a generic 90-day rule`,
          `If your average buyer returns at 45 days, fire your win-back at 60–90 — not the standard "90 days inactive" applied to everyone. Consumables and durables need different cycles; one-size-fits-all leaves money on the table.`, 'reengage'],
        ['5.14', `Calibrate lapsed-customer incentives by LTV`,
          `A high-LTV lapsed buyer deserves a better comeback offer than a low-LTV one. Sending the same 10% off to both undervalues your best customers and overspends on low-value ones.`, 'reengage'],
      ]},
    { id: 6, weight: 0.75, title: 'SMS & conversational channels',
      blurb: `Treat SMS as conversation, not broadcast.`,
      items: [
        ['6.1', `Tag SMS opt-ins by where they signed up`,
          `Someone who opted in via popup is different from someone who opted in at checkout or via a back-in-stock alert. Tag each new subscriber with their source so the first message they get matches their intent. Capturing the number is half the work — knowing why they gave it to you is the other half.`, 'first'],
        ['6.2', `Run an SMS welcome series with channel-appropriate copy`,
          `After someone opts in, send 1–3 welcome texts: discount code, brand intro, cadence expectations. The structure mirrors email welcome flows, but the copy must be shorter and sharper — SMS isn't a place for paragraphs.`, 'first'],
        ['6.3', `Use SMS as the second touch in cart abandonment recovery`,
          `After an unread cart abandonment email at 1hr, follow up with SMS to opted-in contacts at 24–48hr. Often the highest-converting touchpoint in the recovery sequence because text open rates are 90%+. The full cross-channel sequence lives in checkpoint 4.8.`, 'cart'],
        ['6.4', `Segment your SMS list by behaviour, not just by opt-in`,
          `Build segments by purchase history, geography, opt-in source, or engagement level — and only send to the segment that should care. Blasting your entire SMS list weekly is the fastest way to spike opt-outs and burn your sender reputation.`, 'post'],
        ['6.5', `Enable two-way SMS conversational flows`,
          `Let customers reply with "STOP", "MORE", or product questions and get an automated or human response. Postscript and Attentive both support this; most merchants only use one-way broadcasts and miss the engagement.`, 'post'],
        ['6.6', `Maintain an auditable record of every SMS consent`,
          `For every SMS subscriber, you should be able to produce a timestamped record of when, where, and how they opted in. TCPA fines start at $500 per non-compliant text and stack quickly. The opt-in flow itself (double confirmation) is covered in checkpoint 10.7 — this one is about the audit trail.`, 'foundation'],
        ['6.7', `Send SMS in the recipient's timezone, not yours`,
          `A 9am send for an East Coast customer is 6am in California. Timezone-aware sending prevents middle-of-the-night texts that destroy your deliverability and trigger opt-outs.`, 'post'],
      ]},
    { id: 7, weight: 0.75, title: 'Onsite messaging, popups & overlays',
      blurb: `More popups ≠ better. Personalization is what makes them tolerable.`,
      items: [
        ['7.1', `Show different popups to new visitors vs returning customers`,
          `A new visitor and a returning customer shouldn't see the same popup. Newcomers might get a first-time offer; returning customers might get a new-arrivals teaser or a loyalty nudge. The gating mechanic (don't show first-time offers to existing customers) is covered in checkpoint 2.9 — this one is about actively varying the content for each.`, 'first'],
        ['7.2', `Tailor exit-intent popups by cart value or page type`,
          `A visitor exiting with $200 in their cart should see a different offer (free shipping, complementary product) than someone exiting from a blog post (newsletter signup). Generic exit popups convert at half the rate of contextual ones.`, 'cart'],
        ['7.3', `Geo-target promotional banners`,
          `Show "Free shipping over €50 in the EU" only to EU visitors. US visitors see "Free US shipping over $50". Same banner real estate, different messages, no confusion about whether the offer applies.`, 'first'],
        ['7.4', `Use behavioural triggers, not time delays, to fire popups`,
          `A popup that fires after 5 seconds for every visitor is broadcast. A popup that fires when a visitor has scrolled 70% of a PDP, or has viewed three products without adding to cart, is personalization. Behavioural triggers convert at multiples of time-based ones because they fire when intent is highest.`, 'discovery'],
        ['7.5', `Match popup offers to what's already in the cart`,
          `If someone has a coffee machine in their cart and triggers an exit-intent popup, offer 10% off coffee beans — not a generic newsletter signup. The popup engine should read the cart contents and adapt the offer. Most stores fire the same popup regardless of what the visitor is about to buy.`, 'cart'],
        ['7.6', `Respect popup frequency caps`,
          `A visitor shouldn't see the same popup three times in one session, and dismissing should suppress it for at least 7 days. Popup spam is the #1 reason visitors install ad blockers and rage-bounce.`, 'first'],
        ['7.7', `Hide email-capture popups from logged-in customers`,
          `They're already on your list — showing them a "Sign up for 10% off" popup signals you don't recognise them. Quick fix with high impact on perceived sophistication.`, 'first'],
        ['7.8', `Vary popup creative by where the visitor came from`,
          `A visitor from Instagram sees "Welcome from IG — here's 15% off." One from a Google search sees "First time at Brand X? Save 10%." Subtle, but it compounds with the source-matching you're already doing on the hero (checkpoint 2.1).`, 'first'],
      ]},
    { id: 8, weight: 1.0, title: 'Loyalty, retention & post-purchase',
      blurb: `Personalization that earns the second order.`,
      items: [
        ['8.1', `Run a tiered loyalty program`,
          `Bronze / Silver / Gold tiers with progressive perks (early access, free shipping, exclusive products). Tiers create aspiration and a reason to spend up. Flat point systems don't generate the same upward pressure.`, 'reengage'],
        ['8.2', `Make tier status visible across every touchpoint, not just the loyalty app`,
          `A Gold-tier customer should see their badge on the homepage banner, in email headers, and on their account page — not buried inside one tool. Status only matters if customers see it at the moments they're making decisions.`, 'reengage'],
        ['8.3', `Display points balance in checkout, account, and emails`,
          `Display "You have 2,400 points (worth $24)" everywhere — but especially at checkout, with a one-tap redemption option. Points that aren't visible at the moment of purchase are points customers forget they have, and points customers forget they have don't drive repeat orders.`, 'cart'],
        ['8.4', `Send replenishment reminders for consumable products`,
          `A skincare customer who bought a 60-day moisturiser should get an email at day 50: "Time to restock?". Subscription-grade retention without forcing customers into a subscription they don't want.`, 'reengage'],
        ['8.5', `Personalize the order confirmation and thank-you pages`,
          `The thank-you page should reference the customer by name, suggest next steps based on what they bought, and feel like a moment — not a sterile receipt screen. This is the highest-trust moment in the funnel; don't waste it.`, 'post'],
        ['8.6', `Add one-click reorder for past purchases`,
          `On the account page, "Order again" buttons next to past items. Critical for consumables and B2B, but useful for any habitual purchase. Removes the cognitive load of remembering what worked last time.`, 'reengage'],
        ['8.7', `Time review requests by product type, not order date`,
          `Review request should fire N days after delivery — not after order placed — and N varies by product type (5 days for a tee, 21 days for skincare). Better timing means more reviews and more honest ones.`, 'post'],
        ['8.8', `Run a refer-a-friend program with personalized share links`,
          `Each customer gets a unique referral link or code, can see their referral status, and gets rewarded when friends purchase. Word-of-mouth as a measurable, trackable acquisition channel.`, 'reengage'],
        ['8.9', `Give subscription customers self-serve management`,
          `Let customers skip a delivery, swap a flavour, change frequency, or pause — without contacting support. Forced support contact is the #1 cause of subscription churn; self-serve dramatically extends LTV.`, 'reengage'],
        ['8.10', `Acknowledge repeat customers in transactional flows`,
          `On a third-order customer's confirmation, show "Thanks for being a regular." On a tenth, "You're one of our top customers." Distinct from any formal loyalty program — costs nothing, changes how the customer feels about the brand.`, 'post'],
      ]},
    { id: 9, weight: 1.5, title: 'Data foundation & tech stack health',
      blurb: `Without this, everything else degrades over time.`,
      items: [
        ['9.1', `Implement server-side tracking`,
          `Send conversion events (purchases, signups, cart adds) from your server via Conversions API or similar — not just from browser pixels. Browser pixels are blocked by ad blockers, iOS privacy, and Safari ITP, leaving 30–50% of conversions invisible to your ad platforms.`, 'foundation'],
        ['9.2', `Verify customer events flow correctly into your tools`,
          `Place a test order quarterly and check it appears in Klaviyo, GA4, and your CDP within minutes — with all the right fields populated. Silent data drift is the most common cause of broken personalization that nobody notices for months.`, 'foundation'],
        ['9.3', `Sync catalog data to every personalization tool`,
          `Price, inventory, attributes (size, colour, category, tags) should match across your recommendation engine, search tool, ads platforms, and email. The most common failure: emails recommending products that went out of stock yesterday.`, 'foundation'],
        ['9.4', `Unify customer identity across systems`,
          `The same customer record should link email, web, mobile, and in-store activity. Usually achieved via a CDP, a unified ID provider, or Shopify's own identity graph. The basic "use one identifier" rule sits in checkpoint 0.3 — this checkpoint is about the technical work to actually unify across devices and sessions.`, 'foundation'],
        ['9.5', `Make first-party data your primary signal`,
          `Run personalization on what customers actively gave you (purchases, declared preferences, email engagement) — not on third-party cookies that are dying. Future-proofs your strategy against every browser-privacy change still to come.`, 'foundation'],
        ['9.6', `Eliminate redundant apps from your stack`,
          `Audit installed apps quarterly. Two recommendation engines fighting on the same PDP slow your site. Three popup tools triggering at once confuse customers. You're paying for tools that overlap in function — and they're actively making each other worse.`, 'foundation'],
        ['9.7', `Document and enforce a tagging schema`,
          `Write down what every customer and product tag means and when it's applied. Make sure the team uses the documented schema. Without it, tags drift into chaos within months — and your segments stop working without anyone realising.`, 'foundation'],
        ['9.8', `Set up real A/B testing infrastructure`,
          `Use tools like Intelligems or Shoplift to run controlled experiments with proper statistical significance — not "we changed the homepage and revenue went up". Causation requires control groups; without them, you're guessing.`, 'foundation'],
        ['9.9', `Attribute revenue lift to specific personalization features`,
          `You should know that the recommendations widget drives X% of revenue, the cart upsell drives Y%, the abandonment flow drives Z%. Without per-feature attribution, you can't justify keeping anything when budget gets cut.`, 'foundation'],
        ['9.10', `Define data retention and deletion policies`,
          `Know how long you keep customer data, when it's automatically deleted, and how customers can request deletion. Required for GDPR and CCPA — but also basic hygiene that limits damage if you're ever breached.`, 'foundation'],
      ]},
    { id: 10, weight: 1.0, title: 'Privacy, consent & trust',
      blurb: `Compliance is non-negotiable; personalization that crosses the creepy line backfires.`,
      items: [
        ['10.1', `Show a compliant cookie consent banner`,
          `Implement GDPR-compliant consent for EU/UK and CCPA-compliant for California, with proper categories (necessary / analytics / marketing) and a real "Reject" option — not a dark-pattern accept-only banner. The "Reject" button must be as easy to find as "Accept".`, 'foundation'],
        ['10.2', `Honour Do-Not-Track and consent withdrawal`,
          `When a customer revokes consent or enables Do Not Track, actually stop tracking and personalising for them. Be able to prove it via audit trail — saying you stopped isn't the same as actually stopping.`, 'foundation'],
        ['10.3', `Be transparent about what data you collect`,
          `Your privacy policy should describe what data you collect and why — and match what you actually collect. Customers should be able to read it without a law degree. Plain language reduces complaints and builds trust.`, 'foundation'],
        ['10.4', `Don't reveal the depth of your tracking`,
          `Personalization shouldn't make customers feel watched. "We see you've been browsing this for 3 days" is technically accurate but emotionally creepy. Show relevance without showing surveillance — the customer should feel known, not monitored.`, 'foundation'],
        ['10.5', `Run GDPR-compliant flows for EU/UK visitors`,
          `Explicit consent for marketing, double opt-in where required, easy unsubscribe in every email, and Data Subject Access Requests honoured within 30 days. GDPR fines reach 4% of global revenue — not a corner to cut.`, 'foundation'],
        ['10.6', `Show CCPA "Do Not Sell or Share" link to California visitors`,
          `If you sell to California and meet the CCPA thresholds (gross revenue >$26.6M, processing data on 100K+ California residents, or deriving 50%+ revenue from selling/sharing personal info), the link must appear in your footer. Penalties run $2,663–$7,988 per violation, per consumer.`, 'foundation'],
        ['10.7', `Double-confirm SMS opt-in`,
          `After a customer opts in via form or checkbox, send a confirmation SMS asking them to reply YES. Required for TCPA compliance, reduces complaint rates, and weeds out fake or accidental signups. The audit trail (proving each consent later) is covered in checkpoint 6.6 — this one is about the double-opt-in mechanic itself.`, 'foundation'],
        ['10.8', `Give customers self-serve access, export, and deletion of their data`,
          `Add "Download my data" and "Delete my account" options to the account page that work without contacting support. Required by GDPR, expected by CCPA, and good hygiene everywhere — also reduces support tickets.`, 'foundation'],
        ['10.9', `Audit your personalization for the helpful-vs-creepy line`,
          `Review every personalization touchpoint for moments that might feel like surveillance — even if technically legal. The rule is: helpful, not watched. Legal personalization can still feel intrusive and backfire; context and timing matter as much as compliance.`, 'foundation'],
      ]},
  ];

  const MOMENTS = [
    { id: 'first', num: 'M.01', name: 'First visit' },
    { id: 'discovery', num: 'M.02', name: 'Discovery' },
    { id: 'cart', num: 'M.03', name: 'Cart' },
    { id: 'post', num: 'M.04', name: 'Post-purchase' },
    { id: 'reengage', num: 'M.05', name: 'Re-engagement' },
  ];

  // Base impact/ease overrides (further adjusted by inputs at compute time)
  const BASE_IMPACT = {
    '0.1': 7, '0.2': 8, '0.3': 7,
    '1.6': 8, '1.9': 9, '1.10': 9, '1.11': 8,
    '2.1': 8, '2.5': 7, '2.7': 8,
    '3.1': 8, '3.2': 8, '3.4': 9, '3.6': 7, '3.11': 9, '3.12': 8,
    '4.1': 9, '4.2': 9, '4.3': 7, '4.7': 8, '4.8': 9, '4.11': 8,
    '5.2': 7, '5.3': 9, '5.4': 8, '5.5': 7, '5.8': 8, '5.13': 8, '5.14': 7,
    '6.3': 7, '6.4': 6,
    '7.2': 7, '7.5': 7, '7.7': 7,
    '8.1': 7, '8.4': 8, '8.6': 7, '8.10': 6,
    '9.1': 9, '9.2': 9, '9.4': 9, '9.6': 6,
    '10.1': 8, '10.5': 8, '10.7': 8, '10.9': 7,
  };
  const BASE_EASE = {
    '0.1': 9, '0.2': 6, '0.3': 8,
    '1.1': 9, '1.4': 6, '1.9': 3, '1.10': 3, '1.11': 6,
    '2.1': 5, '2.4': 8, '2.7': 7,
    '3.1': 9, '3.2': 8, '3.3': 9, '3.4': 8, '3.11': 9, '3.12': 7,
    '4.2': 9, '4.7': 9, '4.8': 7, '4.11': 6,
    '5.1': 7, '5.3': 8, '5.4': 6, '5.10': 9, '5.13': 6, '5.14': 5,
    '6.1': 8, '6.4': 6, '6.5': 4,
    '7.1': 8, '7.7': 8,
    '8.1': 5, '8.4': 6, '8.6': 6, '8.10': 9,
    '9.1': 4, '9.4': 3, '9.6': 6, '9.8': 7,
    '10.1': 8, '10.7': 8, '10.9': 8,
  };

  // ========== STATE ==========
  let state = {
    dataState: null,
    customerModel: null,
    markets: { eu: null, ca: null, multi: null, other_intl: null },
    aov: null,
    stack: { esp: null, sms: null, loyalty: null, quiz: null, cdp: null, recs: null },
    metrics: {},   // { metric_id: { value: number, skipped: bool } }
    statuses: {},
    suggestedStatuses: {},  // populated from stack toggles
    evidence: {},
    notes: {},
    expanded: {},
    activeHorizon: 'today',
  };

  const STORAGE_KEY = 'shopify-personalization-audit-v4';

  async function saveState() {
    try {
      if (window.storage) {
        await window.storage.set(STORAGE_KEY, JSON.stringify(state));
        updateLastSaved();
      }
    } catch (e) { console.error('Save failed:', e); }
  }
  async function loadState() {
    try {
      if (window.storage) {
        const result = await window.storage.get(STORAGE_KEY);
        if (result && result.value) {
          state = { ...state, ...JSON.parse(result.value) };
          updateLastSaved();
        }
      }
    } catch (e) { /* first run */ }
  }
  function updateLastSaved() {
    const el = document.getElementById('last-saved');
    if (el) {
      const now = new Date();
      el.textContent = '● Saved ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }
  let saveTimer;
  function debouncedSave() { clearTimeout(saveTimer); saveTimer = setTimeout(saveState, 500); }
  function toast(msg) {
    const t = document.getElementById('toast');
    t.textContent = msg;
    t.classList.add('show');
    setTimeout(() => t.classList.remove('show'), 2000);
  }
  function escapeAttr(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }

  // ========== INTAKE RENDER ==========
  function renderIntake() {
    // Data state cards
    const sg = document.getElementById('state-grid');
    sg.innerHTML = '';
    DATA_STATES.forEach(s => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'pick-card' + (state.dataState === s.id ? ' active' : '') + (s.id === 'skip' ? ' skip' : '');
      card.dataset.id = s.id;
      card.innerHTML = `<div class="pc-num">${s.num}</div><div class="pc-name">${s.name}</div><div class="pc-desc">${s.desc}</div>`;
      card.addEventListener('click', () => {
        state.dataState = state.dataState === s.id ? null : s.id;
        debouncedSave();
        renderIntake();
        updateAllSummaries();
      });
      sg.appendChild(card);
    });

    // Customer model
    const mg = document.getElementById('model-grid');
    mg.innerHTML = '';
    CUSTOMER_MODELS.forEach(m => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'pick-card' + (state.customerModel === m.id ? ' active' : '') + (m.id === 'skip' ? ' skip' : '');
      card.innerHTML = `<div class="pc-name">${m.name}</div><div class="pc-desc">${m.desc}</div>`;
      card.addEventListener('click', () => {
        state.customerModel = state.customerModel === m.id ? null : m.id;
        debouncedSave();
        renderIntake();
        renderChecklist();
        updateAllSummaries();
      });
      mg.appendChild(card);
    });

    // Markets toggles
    const mt = document.getElementById('markets-toggles');
    mt.innerHTML = '';
    MARKETS.forEach(mkt => {
      const cur = state.markets[mkt.id];
      const row = document.createElement('div');
      row.className = 'toggle-line';
      row.innerHTML = `
        <div class="toggle-info">
          <div class="name">${mkt.name}</div>
          <div class="hint">${mkt.hint}</div>
        </div>
        <div class="toggle-buttons">
          <button class="tg-btn ${cur === 'yes' ? 'active yes' : ''}" data-mkt="${mkt.id}" data-val="yes">Yes</button>
          <button class="tg-btn ${cur === 'no' ? 'active no' : ''}" data-mkt="${mkt.id}" data-val="no">No</button>
          <button class="tg-btn ${cur === 'skip' ? 'active skip' : ''}" data-mkt="${mkt.id}" data-val="skip">Not sure</button>
        </div>
      `;
      mt.appendChild(row);
    });
    // (event listener for markets is registered once in setupListeners)

    // AOV
    const ag = document.getElementById('aov-grid');
    ag.innerHTML = '';
    AOV_BRACKETS.forEach(b => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'pick-card' + (state.aov === b.id ? ' active' : '') + (b.id === 'skip' ? ' skip' : '');
      card.innerHTML = `<div class="pc-name">${b.name}</div><div class="pc-desc">${b.desc}</div>`;
      card.addEventListener('click', () => {
        state.aov = state.aov === b.id ? null : b.id;
        debouncedSave();
        renderIntake();
        updateAllSummaries();
      });
      ag.appendChild(card);
    });

    // Stack toggles
    const st = document.getElementById('stack-toggles');
    st.innerHTML = '';
    STACK_TOOLS.forEach(t => {
      const cur = state.stack[t.id];
      const row = document.createElement('div');
      row.className = 'toggle-line';
      row.innerHTML = `
        <div class="toggle-info">
          <div class="name">${t.name}</div>
          <div class="hint">${t.hint}</div>
        </div>
        <div class="toggle-buttons">
          <button class="tg-btn ${cur === 'yes' ? 'active yes' : ''}" data-stack="${t.id}" data-val="yes">Yes</button>
          <button class="tg-btn ${cur === 'no' ? 'active no' : ''}" data-stack="${t.id}" data-val="no">No</button>
          <button class="tg-btn ${cur === 'skip' ? 'active skip' : ''}" data-stack="${t.id}" data-val="skip">Not sure</button>
        </div>
      `;
      st.appendChild(row);
    });
    // (event listener for stack is registered once in setupListeners)

    // Performance metrics
    const ml = document.getElementById('metrics-list');
    ml.innerHTML = '';
    PERF_METRICS.forEach(m => {
      const cur = state.metrics[m.id] || {};
      const skipped = cur.skipped === true;
      const card = document.createElement('div');
      card.className = 'metric-card';
      card.innerHTML = `
        <div class="metric-head">
          <div class="metric-name">${m.name}</div>
          <div class="metric-window">${m.window}</div>
        </div>
        <div class="metric-hint">${m.hint}</div>
        <div class="metric-input-row">
          <div class="metric-effect">${m.effect}</div>
          <div class="metric-input-wrap ${skipped ? 'skipped' : ''}">
            <input type="text" inputmode="decimal" class="metric-input ${skipped ? 'skipped' : ''}"
                   data-metric="${m.id}"
                   placeholder="${m.placeholder}"
                   value="${skipped ? '' : (cur.value ?? '')}"
                   ${skipped ? 'disabled' : ''}>
            <span class="metric-suffix">${m.suffix}</span>
          </div>
          <button class="metric-skip-btn ${skipped ? 'active' : ''}" data-skip="${m.id}">${skipped ? '✓ Not sure' : 'Not sure'}</button>
        </div>
      `;
      ml.appendChild(card);
    });
    // (event listeners for metrics are registered once in setupListeners)
  }

  // ========== SUGGESTIONS ==========
  function recomputeSuggestions() {
    state.suggestedStatuses = {};
    STACK_TOOLS.forEach(t => {
      if (state.stack[t.id] === 'yes') {
        t.suggests.forEach(cpId => {
          // Only suggest if user hasn't already set a status
          if (!state.statuses[cpId]) {
            state.suggestedStatuses[cpId] = 'partial';
          }
        });
      }
    });
  }

  // ========== CHECKLIST ==========
  function renderChecklist() {
    const c = document.getElementById('categories-container');
    c.innerHTML = '';

    // suggestion banner
    const sCount = Object.keys(state.suggestedStatuses).filter(id => !state.statuses[id]).length;
    const banner = document.getElementById('suggestion-banner');
    if (sCount > 0) {
      banner.classList.remove('hidden');
      document.getElementById('sb-title').textContent = `${sCount} checkpoint${sCount === 1 ? '' : 's'} pre-suggested as Partial`;
      document.getElementById('sb-sub').textContent = 'Based on the tools you said you have. Click any pill to confirm or change.';
    } else {
      banner.classList.add('hidden');
    }

    CATEGORIES.forEach(cat => {
      const card = document.createElement('div');
      card.className = 'category-card' + (cat.isFoundation ? ' foundation' : '');
      card.dataset.cat = cat.id;
      const score = computeCategoryScore(cat);
      card.innerHTML = `
        <div class="cat-head" data-toggle-cat="${cat.id}">
          <div class="cat-title-wrap">
            <div class="cat-num">${String(cat.id).padStart(2,'0')}</div>
            <div>
              <div class="cat-title">${cat.title}</div>
              <div class="cat-subtitle">${cat.blurb}</div>
            </div>
          </div>
          <div class="cat-score-badge ${score === null ? 'empty' : ''}">${score === null ? '— /100' : score + '/100'}</div>
        </div>
        <div class="cat-body" id="catbody-${cat.id}"></div>
      `;
      c.appendChild(card);
      const body = card.querySelector('.cat-body');

      cat.items.forEach(([id, text, desc]) => {
        const cp = document.createElement('div');
        cp.className = 'check-row';
        cp.dataset.cp = id;
        const status = state.statuses[id];
        const suggested = !status && state.suggestedStatuses[id];
        if (status) cp.classList.add('has-status');
        if (suggested) cp.classList.add('has-suggestion');
        if (state.expanded[id]) cp.classList.add('expanded');

        cp.innerHTML = `
          <div class="check-id">${id}</div>
          <div>
            <div class="check-text">${text}</div>
            <div class="check-desc">${desc}</div>
            <div class="check-extra ${state.expanded[id] ? 'visible' : ''}">
              <div class="extra-field">
                <div class="extra-label">Evidence (boosts confidence in your gap analysis)</div>
                <textarea data-evidence="${id}" placeholder="What did you observe? What's the data?">${escapeAttr(state.evidence[id] || '')}</textarea>
              </div>
            </div>
          </div>
          <div class="check-pills">
            ${makePill(id, 'done', (window.AUDIT_LABELS && window.AUDIT_LABELS.done) || 'Done', status, suggested)}
            ${makePill(id, 'partial', (window.AUDIT_LABELS && window.AUDIT_LABELS.partial) || 'Partial', status, suggested)}
            ${makePill(id, 'missing', (window.AUDIT_LABELS && window.AUDIT_LABELS.missing_short) || 'Miss', status, suggested)}
            ${makePill(id, 'na', (window.AUDIT_LABELS && window.AUDIT_LABELS.na) || 'N/A', status, suggested)}
          </div>
        `;
        body.appendChild(cp);
      });
    });

    // (event listeners for checklist are registered once in setupListeners)
  }

  function makePill(id, status, label, currentStatus, suggested) {
    const isActive = currentStatus === status;
    const isSuggested = !currentStatus && suggested === status;
    let cls = 'check-pill';
    if (isActive) cls += ' active ' + status;
    else if (isSuggested) cls += ' suggested ' + status;
    return `<button class="${cls}" data-status-id="${id}" data-status="${status}">${label}</button>`;
  }

  function rerenderCheckpoint(id) {
    const cp = document.querySelector(`.check-row[data-cp="${id}"]`);
    if (!cp) return;
    const status = state.statuses[id];
    cp.classList.toggle('has-status', !!status);
    cp.classList.remove('has-suggestion');  // user has acted
    if (!status) {
      state.expanded[id] = false;
      cp.classList.remove('expanded');
      const ex = cp.querySelector('.check-extra');
      if (ex) ex.classList.remove('visible');
    }
    cp.querySelectorAll('.check-pill').forEach(p => {
      p.className = 'check-pill';
      if (p.dataset.status === status) p.classList.add('active', status);
    });
    const catId = parseInt(id.split('.')[0]);
    const cat = CATEGORIES.find(c => c.id === catId);
    const pill = document.querySelector(`.category-card[data-cat="${catId}"] .cat-score-badge`);
    const score = computeCategoryScore(cat);
    pill.classList.toggle('empty', score === null);
    pill.textContent = score === null ? '— /100' : score + '/100';
  }

  // ========== SCORING ==========
  function pointsFor(status) {
    if (status === 'done') return 1;
    if (status === 'partial') return 0.5;
    return 0;
  }

  // Determine effective status, applying auto-N/A from markets
  function effectiveStatus(id) {
    // Markets-driven N/A overrides
    if (id === '10.5' && state.markets.eu === 'no') return 'na';
    if (id === '10.6' && state.markets.ca === 'no') return 'na';
    // Geo-detection only N/A when not selling to multi-country AND no other international
    if (id === '1.3' && state.markets.multi === 'no' && state.markets.other_intl === 'no') return 'na';
    // Customer-model-driven N/A defaults
    if (state.customerModel === 'b2b') {
      if (id === '4.6' && !state.statuses[id]) return 'na';
      if (id === '4.7' && !state.statuses[id]) return 'na';
    }
    return state.statuses[id] || null;
  }

  // Effective category weight (adjusts by customer model)
  function effectiveCategoryWeight(cat) {
    let w = cat.weight;
    if (state.customerModel === 'b2b') {
      if (cat.id === 7) w = 0.5;
    }
    return w;
  }

  // Per-item weight multiplier (adjusts by customer model)
  function itemWeightMult(itemId) {
    if (state.customerModel === 'b2b') {
      if (itemId === '1.7') return 2.0;
    }
    if (state.customerModel === 'mix' || state.customerModel === 'mix-sub') {
      if (itemId === '1.7') return 1.5;
    }
    if (state.customerModel === 'sub' || state.customerModel === 'mix-sub') {
      if (itemId === '8.4' || itemId === '8.9') return 1.5;
      if (itemId === '4.11') return 0.7;
    }
    return 1.0;
  }

  function computeCategoryScore(cat) {
    let weightSum = 0, weightedPoints = 0;
    cat.items.forEach(([id]) => {
      const s = effectiveStatus(id);
      if (!s || s === 'na') return;
      const w = itemWeightMult(id);
      weightSum += w;
      weightedPoints += pointsFor(s) * w;
    });
    if (weightSum === 0) return null;
    return Math.round((weightedPoints / weightSum) * 100);
  }

  function computeOverallScore() {
    let weightSum = 0, weightedSum = 0, anyScored = false;
    CATEGORIES.forEach(cat => {
      const s = computeCategoryScore(cat);
      if (s === null) return;
      anyScored = true;
      const w = effectiveCategoryWeight(cat);
      weightedSum += s * w;
      weightSum += w;
    });
    return anyScored ? Math.round(weightedSum / weightSum) : null;
  }

  function getTier(score) {
    if (score === null) return { name: 'Awaiting Inputs', desc: 'Complete the checklist to unlock your maturity assessment.' };
    if (score <= 25) return { name: 'Foundational', desc: 'Default Shopify setup with little or no personalization beyond what comes out of the box.' };
    if (score <= 50) return { name: 'Developing', desc: 'Some segmentation and basic email flows in place, but personalization is shallow and inconsistent.' };
    if (score <= 75) return { name: 'Intermediate', desc: 'Multi-channel personalization with configured recs and segmented flows. Real lift, but ceiling is visible.' };
    if (score <= 90) return { name: 'Advanced', desc: 'CDP-backed orchestration across channels. Tested, attributed, continuously improving.' };
    return { name: 'Best-in-Class', desc: 'Predictive, real-time, instrumented end-to-end. Personalization is a system, not a feature.' };
  }

  function computeMomentScore(momentId) {
    let applicable = 0, points = 0;
    CATEGORIES.forEach(cat => {
      cat.items.forEach(([id, , , tag]) => {
        if (tag !== momentId) return;
        const s = effectiveStatus(id);
        if (!s || s === 'na') return;
        applicable++;
        points += pointsFor(s);
      });
    });
    if (applicable === 0) return null;
    return Math.round((points / applicable) * 100);
  }

  // Effective Impact: base + AOV + perf metric adjustments
  function effectiveImpact(itemId) {
    let impact = BASE_IMPACT[itemId] || 6;
    // AOV adjustments
    if (state.aov === 'low' && ['4.2', '4.3', '7.5'].includes(itemId)) impact += 1;
    if (state.aov === 'high' && itemId.startsWith('4.')) impact += 1;
    // Performance metric adjustments
    const m = state.metrics;
    if (m.cr_overall && !m.cr_overall.skipped && m.cr_overall.value !== null) {
      // diagnosis copy effect, not impact
    }
    if (m.cr_mobile && !m.cr_mobile.skipped && m.cr_mobile.value !== null
        && m.cr_overall && !m.cr_overall.skipped && m.cr_overall.value !== null) {
      const desktopApprox = m.cr_overall.value * 1.4;  // rough estimate
      if (m.cr_mobile.value < 0.6 * desktopApprox && itemId === '1.4') impact += 2;
    }
    if (m.repeat_rate && !m.repeat_rate.skipped && m.repeat_rate.value !== null) {
      if (m.repeat_rate.value < 20 && (itemId === '5.5' || itemId === '5.13' || itemId === '5.14' || itemId.startsWith('8.'))) {
        impact += 1;
      }
    }
    if (m.cart_abandon && !m.cart_abandon.skipped && m.cart_abandon.value !== null) {
      if (m.cart_abandon.value > 70 && itemId.startsWith('4.')) impact += 1;
    }
    if (m.email_pct && !m.email_pct.skipped && m.email_pct.value !== null) {
      if (m.email_pct.value < 15 && itemId.startsWith('5.')) impact += 1;
    }
    return Math.min(10, impact);
  }

  // ========== SCORECARD ==========
  function renderScorecard() {
    const overall = computeOverallScore();
    document.getElementById('overall-num').textContent = overall === null ? '—' : overall;
    const tier = getTier(overall);
    document.getElementById('tier-name').textContent = tier.name;
    document.getElementById('tier-desc').textContent = tier.desc;

    const bars = document.getElementById('cat-bars');
    bars.innerHTML = '';
    CATEGORIES.forEach(cat => {
      const score = computeCategoryScore(cat);
      const row = document.createElement('div');
      row.className = 'cat-bar';
      row.innerHTML = `
        <div class="cb-num">${String(cat.id).padStart(2,'0')}</div>
        <div class="cb-name">${cat.title}</div>
        <div class="cb-track"><div class="cb-fill ${scoreTierClass(score)}" style="width: ${score === null ? 0 : score}%"></div></div>
        <div class="cb-score">${score === null ? '—' : score}</div>
      `;
      bars.appendChild(row);
    });

    const mg = document.getElementById('moments-grid');
    mg.innerHTML = '';
    MOMENTS.forEach(m => {
      const score = computeMomentScore(m.id);
      const card = document.createElement('div');
      card.className = 'moment-card';
      card.innerHTML = `
        <div class="m-num">${m.num}</div>
        <div class="m-name">${m.name}</div>
        <div class="m-score ${score === null ? 'empty' : ''}">${score === null ? '—' : score}<small>/100</small></div>
      `;
      mg.appendChild(card);
    });
  }

  // ========== EVALUATION ==========
  function renderEvaluation() {
    const container = document.getElementById('evaluation-content');
    const overall = computeOverallScore();
    const totalScored = Object.keys(state.statuses).length;

    if (totalScored < 10) {
      container.innerHTML = `<div class="eval-block"><div class="empty-state">Score at least ten checkpoints to unlock the verdict.<div class="num">${totalScored} /104 SCORED</div></div></div>`;
      return;
    }

    const tier = getTier(overall);
    const gaps = computeGapList();
    const ranked = CATEGORIES.map(c => ({ cat: c, score: computeCategoryScore(c) })).filter(x => x.score !== null);
    const weakest = [...ranked].sort((a, b) => a.score - b.score).slice(0, 3);
    const strongest = [...ranked].sort((a, b) => b.score - a.score).slice(0, 3);

    const today = gaps.filter(g => g.ease >= 8);
    const thisWeek = gaps.filter(g => g.ease >= 5 && g.ease < 8);
    const thisQuarter = gaps.filter(g => g.ease < 5);

    const declared = state.dataState && state.dataState !== 'skip' ? state.dataState : null;
    const inferred = inferDataState();
    const stateMatch = declared && declared !== inferred;

    let html = '';

    html += `<div class="eval-block dark" ${verdictBlockStyle('diagnosis')}>
      <div class="eval-eyebrow"></div>
      <h3>${diagnosisHeadline(overall, tier, weakest)}</h3>
      <p>${diagnosisBody(overall, tier, weakest, strongest, gaps)}</p>
      <div class="data-state-callout">
        <div class="label">Data state ${declared ? '— self-declared & inferred' : '— inferred'}</div>
        <div class="name">${dataStateLabel(inferred)}</div>
        <div class="desc">${stateDesc(inferred, declared, stateMatch)}</div>
      </div>
      ${(window.AUDIT_VERDICT && window.AUDIT_VERDICT.diagnosis && window.AUDIT_VERDICT.diagnosis.link_url) ? `<div class="diagnosis-link"><a href="${window.AUDIT_VERDICT.diagnosis.link_url}" target="_blank" rel="noopener">${verdictText('diagnosis', 'link_text', 'Read about article about eCommerce personalization here')} <span aria-hidden="true">→</span></a></div>` : ''}
    </div>`;

    if (strongest.length && strongest[0].score >= 50) {
      html += `<div class="eval-block" ${verdictBlockStyle('strengths')}>
        <div class="eval-eyebrow">${verdictText('strengths', 'eyebrow_text', '§ II — Strengths')}</div>
        <h3>${verdictText('strengths', 'heading_text', "What's holding up your storefront")}</h3>
        <ul class="gap-list">`;
      strongest.forEach(s => {
        html += `<li class="gap-item"><div><div class="gap-text">${s.cat.title}</div><div class="gap-meta">Category ${String(s.cat.id).padStart(2,'0')} · ${s.cat.items.length} checkpoints</div></div><div class="gap-ice">${s.score}/100</div></li>`;
      });
      html += `</ul></div>`;
    }

    html += `<div class="eval-block" ${verdictBlockStyle('backlog')}>
      <div class="eval-eyebrow">${verdictText('backlog', 'eyebrow_text', '§ III — Backlog by horizon')}</div>
      <h3>${verdictText('backlog', 'heading_text', 'What to do, in what order')}</h3>
      <p>${verdictText('backlog', 'description_text', "Sorted by effort. Today's items can usually be fixed without a developer.")}</p>
      <div class="horizon-tabs">
        <button class="horizon-tab ${state.activeHorizon === 'today' ? 'active' : ''}" data-horizon="today">Today <span class="count">${today.length}</span></button>
        <button class="horizon-tab ${state.activeHorizon === 'week' ? 'active' : ''}" data-horizon="week">This week <span class="count">${thisWeek.length}</span></button>
        <button class="horizon-tab ${state.activeHorizon === 'quarter' ? 'active' : ''}" data-horizon="quarter">This quarter <span class="count">${thisQuarter.length}</span></button>
      </div>
      <ul class="gap-list">`;
    const activeList = state.activeHorizon === 'today' ? today : state.activeHorizon === 'week' ? thisWeek : thisQuarter;
    if (activeList.length === 0) {
      html += `<li style="padding: 24px 0; color: var(--mute); font-style: italic;">No items in this horizon yet.</li>`;
    } else {
      activeList.slice(0, 12).forEach(g => {
        html += `<li class="gap-item">
          <div><div class="gap-text">${g.text}</div><div class="gap-meta">${g.id} · ${g.catTitle} · ${g.statusLabel} · Ease ${g.ease}/10</div></div>
          <div class="gap-ice" data-tip="${ICE_TIP}">ICE ${g.ice.toFixed(1)}</div>
        </li>`;
      });
    }
    html += `</ul></div>`;

    if (gaps.length) {
      html += `<div class="eval-block" ${verdictBlockStyle('gaplog')}>
        <div class="eval-eyebrow">${verdictText('gaplog', 'eyebrow_text', '§ IV — Full gap log')} <span style="color: var(--mute); font-weight: 500;">(top ${Math.min(15, gaps.length)} of ${gaps.length})</span></div>
        <h3>All gaps, ranked by ICE</h3>
        <ul class="gap-list">`;
      gaps.slice(0, 15).forEach(g => {
        html += `<li class="gap-item">
          <div><div class="gap-text">${g.text}</div><div class="gap-meta">${g.id} · ${g.statusLabel} · I ${g.impact} / C ${g.confidence} / E ${g.ease}</div></div>
          <div class="gap-ice" data-tip="${ICE_TIP}">${g.ice.toFixed(1)}</div>
        </li>`;
      });
      html += `</ul></div>`;
    }

    // AI analysis block — always shown when there's a verdict to take to an LLM
    html += `<div class="ai-analyze-block" ${verdictBlockStyle('takeitdeeper')}>
      <div class="ai-eyebrow">${verdictText('takeitdeeper', 'eyebrow_text', '§ VI — Take it deeper')}</div>
      <h3>${verdictText('takeitdeeper', 'heading_text', 'Get a comprehensive review from an AI assistant')}</h3>
      <p>${verdictText('takeitdeeper','description_text', `Your audit is a snapshot. To turn it into a benchmarked, prioritized roadmap, run it through an AI. Click any button below — we'll download your results as a JSON file and open the assistant in a new tab. Attach the file in the chat, then send. The AI returns a 5-part review: maturity overview, KPI interpretation vs. benchmarks, weak-section drill-down, strong-section protect-or-extend actions, and a 30/60/90-day timeline.`)}</p>
      <div class="ai-buttons">
        <button class="ai-btn claude" data-ai="claude">
          <div class="ai-icon">C</div>
          <div class="ai-info">
            <div class="ai-name">Claude</div>
            <div class="ai-cta">Copy + open →</div>
          </div>
        </button>
        <button class="ai-btn chatgpt" data-ai="chatgpt">
          <div class="ai-icon">G</div>
          <div class="ai-info">
            <div class="ai-name">ChatGPT</div>
            <div class="ai-cta">Copy + open →</div>
          </div>
        </button>
        <button class="ai-btn gemini" data-ai="gemini">
          <div class="ai-icon">✦</div>
          <div class="ai-info">
            <div class="ai-name">Gemini</div>
            <div class="ai-cta">Copy + open →</div>
          </div>
        </button>
      </div>
      <div class="ai-howto"><strong>How it works:</strong> click a button → a modal opens showing the review prompt → click "Open [AI]" to download your audit JSON and launch the chat in a new tab → in the chat, attach the JSON file (paperclip icon), paste the prompt, send. Same buttons live in the top navigation bar.</div>
    </div>`;

    container.innerHTML = html;

    container.querySelectorAll('.horizon-tab').forEach(t => {
      t.addEventListener('click', () => {
        state.activeHorizon = t.dataset.horizon;
        debouncedSave();
        renderEvaluation();
      });
    });

    container.querySelectorAll('.ai-btn').forEach(btn => {
      btn.addEventListener('click', () => sendToAI(btn.dataset.ai));
    });
  }

  function diagnosisHeadline(overall, tier, weakest) {
    if (overall === null) return 'Not enough data to diagnose.';
    if (!weakest.length) return `You're at ${tier.name.toLowerCase()} maturity.`;
    const w = weakest[0];
    return `${tier.name} maturity — held back primarily by ${w.cat.title.toLowerCase()}.`;
  }

  function diagnosisBody(overall, tier, weakest, strongest, gaps) {
    if (overall === null) return '';
    const parts = [];
    parts.push(tier.desc);

    // Performance metric callouts
    const m = state.metrics;
    if (m.cr_overall && !m.cr_overall.skipped && m.cr_overall.value !== null && m.cr_overall.value < 1.5) {
      parts.push(`At <strong>${m.cr_overall.value}% conversion</strong>, your priority should be foundational basics — geo, identification, cart-threshold messaging — before sophisticated personalization layers can return their full value.`);
    }
    if (m.cart_abandon && !m.cart_abandon.skipped && m.cart_abandon.value !== null && m.cart_abandon.value > 70) {
      parts.push(`Cart abandonment at <strong>${m.cart_abandon.value}%</strong> is high — cart and checkout items have been weighted up in your gap log.`);
    }
    if (m.repeat_rate && !m.repeat_rate.skipped && m.repeat_rate.value !== null && m.repeat_rate.value < 20) {
      parts.push(`A <strong>${m.repeat_rate.value}% repeat rate</strong> is below the threshold where retention items pay back fastest — they've been prioritised.`);
    }
    if (m.email_pct && !m.email_pct.skipped && m.email_pct.value !== null && m.email_pct.value < 15) {
      parts.push(`Email at <strong>${m.email_pct.value}% of revenue</strong> is below where most mature stores sit (15–30%) — email items get extra weight in the verdict.`);
    }

    if (strongest.length && strongest[0].score >= 60) {
      parts.push(`The audit found relative strength in <strong>${strongest[0].cat.title.toLowerCase()}</strong> (${strongest[0].score}/100) — a foundation to build on.`);
    }
    if (weakest.length && weakest[0].score < 40) {
      parts.push(`The most pressing gap is <strong>${weakest[0].cat.title.toLowerCase()}</strong> at ${weakest[0].score}/100 — biggest absolute lift if you invest there first.`);
    }
    const missingCount = Object.values(state.statuses).filter(s => s === 'missing').length;
    const partialCount = Object.values(state.statuses).filter(s => s === 'partial').length;
    parts.push(`Across the audit, <strong>${missingCount}</strong> checkpoint${missingCount === 1 ? '' : 's'} ${missingCount === 1 ? 'was' : 'were'} marked missing and <strong>${partialCount}</strong> partial.`);
    return parts.join(' ');
  }

  function inferDataState() {
    const has = id => state.statuses[id] === 'done';
    const partial = id => state.statuses[id] === 'partial';
    if (has('3.12') && (has('1.9') || has('1.10') || partial('1.9'))) return 'state3';
    if ((has('1.5') || partial('1.5')) && (has('5.4') || has('5.5') || has('1.6'))) return 'state2';
    return 'state1';
  }

  function dataStateLabel(id) {
    const s = DATA_STATES.find(x => x.id === id);
    return s ? s.name : null;
  }

  function stateDesc(inferred, declared, mismatch) {
    const base = {
      state1: 'Your tactic set is geo, device, traffic source, session behaviour, and trust signals. You can personalize meaningfully without a customer account — most stores ignore this and miss 80% of their traffic.',
      state2: 'You have email, order data, and category preferences from real customers. The biggest wins come from removing already-bought items from upsells, segmenting by spend tier, and breaking post-purchase silence.',
      state3: 'You have quiz answers, preference centre data, browse history, and email engagement signals. The challenge is orchestration — making sure the data drives the experience, not just the segment list.',
    };
    let desc = base[inferred];
    if (mismatch) {
      desc += ` <strong>Note:</strong> you self-declared <strong>${dataStateLabel(declared)}</strong>, but your checkpoint pattern looks more like <strong>${dataStateLabel(inferred)}</strong>. Worth a closer look — usually means tooling exists but isn't operational.`;
    }
    return desc;
  }

  function computeGapList() {
    const gaps = [];
    CATEGORIES.forEach(cat => {
      cat.items.forEach(([id, text]) => {
        const status = effectiveStatus(id);
        if (status !== 'missing' && status !== 'partial') return;
        const impact = effectiveImpact(id);
        const ease = BASE_EASE[id] || 6;
        const confidence = state.evidence[id] && state.evidence[id].length > 10 ? 8 : 6;
        const easeAdj = status === 'partial' ? Math.min(10, ease + 1) : ease;
        const ice = (impact + confidence + easeAdj) / 3;
        gaps.push({
          id, text, catTitle: cat.title,
          statusLabel: status === 'missing' ? 'MISSING' : 'PARTIAL',
          impact, confidence, ease: easeAdj, ice
        });
      });
    });
    gaps.sort((a, b) => b.ice - a.ice);
    return gaps;
  }

  // ========== SUMMARIES ==========
  function updateAllSummaries() {
    const counts = { done: 0, partial: 0, missing: 0, na: 0 };
    Object.values(state.statuses).forEach(s => { if (counts[s] !== undefined) counts[s]++; });
    // Add auto-NA from markets/model
    CATEGORIES.forEach(cat => {
      cat.items.forEach(([id]) => {
        if (state.statuses[id]) return;
        const eff = effectiveStatus(id);
        if (eff === 'na') counts.na++;
      });
    });
    document.getElementById('stat-done').textContent = counts.done;
    document.getElementById('stat-partial').textContent = counts.partial;
    document.getElementById('stat-missing').textContent = counts.missing;
    document.getElementById('stat-na').textContent = counts.na;

    const total = Object.keys(state.statuses).length;
    const totalItems = CATEGORIES.reduce((sum, c) => sum + c.items.length, 0);
    const pct = Math.round((total / totalItems) * 100);
    const fillEl = document.getElementById('progress-fill');
    fillEl.style.width = pct + '%';
    // Score-based color tier on the progress bar
    const _overallForBar = computeOverallScore();
    ['tier-foundational','tier-developing','tier-intermediate','tier-advanced','tier-best']
      .forEach(c => fillEl.classList.remove(c));
    if (_overallForBar !== null) fillEl.classList.add(scoreTierClass(_overallForBar));
    document.getElementById('progress-text').textContent = total + ' / ' + totalItems;

    renderScorecard();
    renderEvaluation();
  }

  // ========== ACTIONS ==========
  // ========== MARKDOWN REPORT ==========
  function buildMarkdownReport() {
    const overall = computeOverallScore();
    const tier = getTier(overall);
    const today = new Date().toISOString().slice(0, 10);

    const lines = [];
    lines.push(`# Shopify Personalization Audit — Report`);
    lines.push(``);
    lines.push(`**Generated:** ${today}`);
    lines.push(``);
    lines.push(`---`);
    lines.push(``);

    // Context
    lines.push(`## Store Context`);
    lines.push(``);
    const dsLabel = state.dataState ? (DATA_STATES.find(s => s.id === state.dataState)?.name || state.dataState) : 'Not specified';
    lines.push(`- **Data state (declared):** ${dsLabel}`);
    const cm = state.customerModel ? (CUSTOMER_MODELS.find(m => m.id === state.customerModel)?.name || state.customerModel) : 'Not specified';
    lines.push(`- **Customer model:** ${cm}`);

    const mktList = [];
    if (state.markets.eu === 'yes') mktList.push('EU/UK');
    else if (state.markets.eu === 'no') mktList.push('not EU/UK');
    if (state.markets.ca === 'yes') mktList.push('California');
    else if (state.markets.ca === 'no') mktList.push('not California');
    if (state.markets.multi === 'yes') mktList.push('multi-country');
    else if (state.markets.multi === 'no') mktList.push('single-country');
    lines.push(`- **Markets:** ${mktList.length ? mktList.join(', ') : 'Not specified'}`);

    const aov = state.aov ? (AOV_BRACKETS.find(b => b.id === state.aov)?.name || state.aov) : 'Not specified';
    lines.push(`- **AOV bracket:** ${aov}`);

    const stackList = STACK_TOOLS.filter(t => state.stack[t.id] === 'yes').map(t => t.name);
    lines.push(`- **Tools in stack:** ${stackList.length ? stackList.join(', ') : 'None reported'}`);

    lines.push(``);
    lines.push(`### Performance Metrics`);
    lines.push(``);
    PERF_METRICS.forEach(m => {
      const v = state.metrics[m.id];
      let display = '_Not provided_';
      if (v && !v.skipped && v.value !== null) display = `${v.value}${m.suffix}`;
      else if (v && v.skipped) display = '_Skipped (not sure)_';
      lines.push(`- **${m.name}** (${m.window}): ${display}`);
    });

    lines.push(``);
    lines.push(`---`);
    lines.push(``);

    // Score
    lines.push(`## Overall Score & Maturity`);
    lines.push(``);
    lines.push(`**Score:** ${overall === null ? '— (insufficient data)' : overall + ' / 100'}`);
    lines.push(``);
    lines.push(`**Maturity tier:** ${tier.name}`);
    lines.push(``);
    lines.push(`> ${tier.desc}`);
    lines.push(``);

    // Inferred state
    const inferred = inferDataState();
    const declared = state.dataState && state.dataState !== 'skip' ? state.dataState : null;
    const stateMatch = declared && declared !== inferred;
    lines.push(`**Inferred data state (from checkpoints):** ${dataStateLabel(inferred)}`);
    if (stateMatch) {
      lines.push(``);
      lines.push(`> ⚠ Mismatch: you self-declared "${dataStateLabel(declared)}" but the checkpoint pattern looks more like "${dataStateLabel(inferred)}". Likely tooling exists but isn't operational.`);
    }
    lines.push(``);
    lines.push(`---`);
    lines.push(``);

    // Per-category scores
    lines.push(`## Scores by Category`);
    lines.push(``);
    lines.push(`| # | Category | Weight | Score |`);
    lines.push(`|---|---|---|---|`);
    CATEGORIES.forEach(cat => {
      const s = computeCategoryScore(cat);
      const w = effectiveCategoryWeight(cat);
      lines.push(`| ${String(cat.id).padStart(2,'0')} | ${cat.title} | ${w}x | ${s === null ? '—' : s + '/100'} |`);
    });
    lines.push(``);

    // Five moments
    lines.push(`## Scores by Journey Moment`);
    lines.push(``);
    lines.push(`| Moment | Score |`);
    lines.push(`|---|---|`);
    MOMENTS.forEach(m => {
      const s = computeMomentScore(m.id);
      lines.push(`| ${m.num} ${m.name} | ${s === null ? '—' : s + '/100'} |`);
    });
    lines.push(``);
    lines.push(`---`);
    lines.push(``);

    // Full checklist with statuses
    lines.push(`## Full Checklist`);
    lines.push(``);
    CATEGORIES.forEach(cat => {
      lines.push(`### ${String(cat.id).padStart(2,'0')}. ${cat.title}`);
      lines.push(``);
      lines.push(`*${cat.blurb}*`);
      lines.push(``);
      cat.items.forEach(([id, text, desc]) => {
        const eff = effectiveStatus(id);
        let icon = '⬜';
        let label = 'Not scored';
        if (eff === 'done') { icon = '✅'; label = (window.AUDIT_LABELS && window.AUDIT_LABELS.done) || 'Done'; }
        else if (eff === 'partial') { icon = '🟧'; label = (window.AUDIT_LABELS && window.AUDIT_LABELS.partial) || 'Partial'; }
        else if (eff === 'missing') { icon = '❌'; label = (window.AUDIT_LABELS && window.AUDIT_LABELS.missing) || 'Missing'; }
        else if (eff === 'na') { icon = '⚪'; label = (window.AUDIT_LABELS && window.AUDIT_LABELS.na) || 'N/A'; }
        lines.push(`- ${icon} **${id}** ${text} — _${label}_`);
        if (state.evidence[id]) {
          lines.push(`  - *Evidence:* ${state.evidence[id].replace(/\n+/g, ' ')}`);
        }
      });
      lines.push(``);
    });

    lines.push(`---`);
    lines.push(``);

    // Diagnosis
    if (overall !== null) {
      const ranked = CATEGORIES.map(c => ({ cat: c, score: computeCategoryScore(c) })).filter(x => x.score !== null);
      const weakest = [...ranked].sort((a, b) => a.score - b.score).slice(0, 3);
      const strongest = [...ranked].sort((a, b) => b.score - a.score).slice(0, 3);

      lines.push(`## Diagnosis`);
      lines.push(``);
      lines.push(`**${diagnosisHeadline(overall, tier, weakest)}**`);
      lines.push(``);
      const body = diagnosisBody(overall, tier, weakest, strongest, computeGapList())
        .replace(/<\/?strong>/g, '**');
      lines.push(body);
      lines.push(``);

      // Gap log
      const gaps = computeGapList();
      if (gaps.length) {
        lines.push(`## ICE-Ranked Gap Log`);
        lines.push(``);
        lines.push(`| # | ID | Gap | Status | Impact | Confidence | Ease | ICE |`);
        lines.push(`|---|---|---|---|---|---|---|---|`);
        gaps.forEach((g, i) => {
          lines.push(`| ${i + 1} | ${g.id} | ${g.text} | ${g.statusLabel} | ${g.impact} | ${g.confidence} | ${g.ease} | ${g.ice.toFixed(1)} |`);
        });
        lines.push(``);

        // Backlog by horizon
        const today_g = gaps.filter(g => g.ease >= 8);
        const week_g = gaps.filter(g => g.ease >= 5 && g.ease < 8);
        const quarter_g = gaps.filter(g => g.ease < 5);

        lines.push(`## Backlog by Horizon`);
        lines.push(``);
        if (today_g.length) {
          lines.push(`### Today (no developer needed)`);
          today_g.forEach(g => lines.push(`- **${g.id}** — ${g.text} (ICE ${g.ice.toFixed(1)})`));
          lines.push(``);
        }
        if (week_g.length) {
          lines.push(`### This week (some configuration)`);
          week_g.forEach(g => lines.push(`- **${g.id}** — ${g.text} (ICE ${g.ice.toFixed(1)})`));
          lines.push(``);
        }
        if (quarter_g.length) {
          lines.push(`### This quarter (real project)`);
          quarter_g.forEach(g => lines.push(`- **${g.id}** — ${g.text} (ICE ${g.ice.toFixed(1)})`));
          lines.push(``);
        }
      }
    }

    return lines.join('\n');
  }

  // ========== JSON REPORT ==========
  // Structured export designed to be read by an LLM. Mirrors the Markdown report
  // but in machine-readable form. Status values are normalised to: done | partial | missing | na | unscored.
  function buildJsonReport() {
    const overall = computeOverallScore();
    const tier = getTier(overall);
    const inferred = inferDataState();
    const declared = state.dataState && state.dataState !== 'skip' ? state.dataState : null;

    // Context blocks
    const dsLabel = state.dataState ? (DATA_STATES.find(s => s.id === state.dataState)?.name || state.dataState) : null;
    const cmLabel = state.customerModel ? (CUSTOMER_MODELS.find(m => m.id === state.customerModel)?.name || state.customerModel) : null;
    const aovLabel = state.aov ? (AOV_BRACKETS.find(b => b.id === state.aov)?.name || state.aov) : null;

    const markets = {
      eu_uk: state.markets.eu || 'not_specified',
      california: state.markets.ca || 'not_specified',
      multi_country: state.markets.multi || 'not_specified',
      other_international: state.markets.other_intl || 'not_specified',
    };

    const stack = {};
    STACK_TOOLS.forEach(t => { stack[t.id] = { name: t.name, status: state.stack[t.id] || 'not_specified' }; });

    // Performance metrics
    const metrics = {};
    PERF_METRICS.forEach(m => {
      const v = state.metrics[m.id];
      metrics[m.id] = {
        name: m.name,
        window: m.window,
        unit: m.suffix || '',
        value: v && !v.skipped && v.value !== null ? v.value : null,
        skipped: !!(v && v.skipped),
        provided: !!(v && !v.skipped && v.value !== null),
      };
    });

    // Per-category data with all checklist items
    const sections = CATEGORIES.map(cat => {
      const score = computeCategoryScore(cat);
      const items = cat.items.map(([id, title, desc, momentTag]) => {
        const rawStatus = state.statuses[id] || null;
        const eff = effectiveStatus(id);
        return {
          id,
          title,
          description: desc,
          journey_moment: momentTag || null,
          status: eff || 'unscored',
          raw_status: rawStatus,
          auto_na: !rawStatus && eff === 'na',
          evidence: state.evidence[id] || null,
          impact_score: effectiveImpact(id),
          ease_score: BASE_EASE[id] || 6,
        };
      });
      const counts = { done: 0, partial: 0, missing: 0, na: 0, unscored: 0 };
      items.forEach(it => { if (counts[it.status] !== undefined) counts[it.status]++; });
      const scorable = items.length - counts.na - counts.unscored;
      const maturity_pct = scorable > 0
        ? Math.round(((counts.done + counts.partial * 0.5) / scorable) * 100)
        : null;
      return {
        id: cat.id,
        title: cat.title,
        weight: effectiveCategoryWeight(cat),
        score_0_100: score,
        maturity_pct,
        is_foundation: !!cat.isFoundation,
        counts,
        items,
      };
    });

    // Five Moments rollup
    const moments = MOMENTS.map(m => ({
      id: m.id,
      number: m.num,
      name: m.name,
      score_0_100: computeMomentScore(m.id),
    }));

    // ICE-ranked gap list
    const gaps = computeGapList().map((g, i) => ({
      rank: i + 1,
      id: g.id,
      title: g.text,
      category: g.catTitle,
      status: g.statusLabel.toLowerCase(),
      impact: g.impact,
      confidence: g.confidence,
      ease: g.ease,
      ice: Number(g.ice.toFixed(2)),
    }));

    // Backlog by horizon
    const backlog = {
      today: gaps.filter(g => g.ease >= 8),
      this_week: gaps.filter(g => g.ease >= 5 && g.ease < 8),
      this_quarter: gaps.filter(g => g.ease < 5),
    };

    return {
      instructions: AI_PROMPT,
      schema_version: 'qikify-personalization-audit/v1',
      generated_at: new Date().toISOString(),
      tool: 'Shopify Personalization Audit by Qikify',
      summary: {
        overall_score_0_100: overall,
        maturity_tier: tier ? { id: tier.id, name: tier.name, description: tier.desc } : null,
        declared_data_state: declared ? { id: declared, label: dsLabel } : null,
        inferred_data_state: { id: inferred, label: dataStateLabel(inferred) },
        data_state_mismatch: !!(declared && declared !== inferred),
      },
      context: {
        customer_model: state.customerModel ? { id: state.customerModel, label: cmLabel } : null,
        aov_bracket: state.aov ? { id: state.aov, label: aovLabel } : null,
        markets,
        tech_stack: stack,
      },
      performance_metrics: metrics,
      sections,
      journey_moments: moments,
      gaps_ranked: gaps,
      backlog_by_horizon: backlog,
    };
  }

  // ========== AI BUTTONS ==========
  // Each provider opens the same step-based modal. Inside the modal, three independent
  // action buttons let the user download the audit JSON, copy the prompt, and open the
  // chat tab — in any order, at their own pace.
  const AI_PROVIDERS = {
    claude:  { name: 'Claude',  url: 'https://claude.ai/new',           iconLetter: 'C' },
    chatgpt: { name: 'ChatGPT', url: 'https://chatgpt.com/',            iconLetter: 'G' },
    gemini:  { name: 'Gemini',  url: 'https://gemini.google.com/app',   iconLetter: '✦' },
  };

  // Currently-selected provider (set when user clicks a Review button)
  let activeAiProvider = null;

  function sendToAI(provider) {
    const cfg = AI_PROVIDERS[provider];
    if (!cfg) return;
    activeAiProvider = provider;

    // Provider-specific branding — reuse the icon from the header button if available
    const icon = document.getElementById('ai-modal-icon');
    icon.className = 'ai-modal-icon ' + provider;
    var headerBtn = document.getElementById('btn-review-' + provider);
    var headerDot = headerBtn ? headerBtn.querySelector('.ai-dot') : null;
    if (headerDot) {
      icon.innerHTML = headerDot.innerHTML;
    } else {
      icon.textContent = cfg.iconLetter;
    }
    document.getElementById('ai-modal-title').textContent = 'Send to ' + cfg.name + ' for a deeper review of your results';
    document.getElementById('ai-modal-name-1').textContent = cfg.name;
    document.getElementById('ai-modal-name-2').textContent = cfg.name;
    document.getElementById('ai-modal-name-3').textContent = cfg.name;

    // Reset all three step button states
    resetStepButton('ai-step-download', '↓ Download result');
    resetStepButton('ai-step-open', '↗ Open ' + cfg.name);

    document.getElementById('ai-modal').classList.add('show');
  }

  function resetStepButton(id, label) {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.classList.remove('copied', 'downloaded');
    // Open button keeps the lime accent always; preserve its label structure with provider name span
    if (id === 'ai-step-open') {
      const cfg = activeAiProvider ? AI_PROVIDERS[activeAiProvider] : null;
      const name = cfg ? cfg.name : 'Claude';
      btn.innerHTML = '↗ Open <span id="ai-modal-name-3">' + name + '</span>';
    } else {
      btn.textContent = label;
    }
  }

  function flashStepDone(btn, label, cls) {
    btn.classList.add(cls);
    btn.textContent = label;
    setTimeout(() => {
      btn.classList.remove(cls);
      // Restore original label
      if (btn.id === 'ai-step-download') btn.textContent = '↓ Download result';
      else if (btn.id === 'ai-step-copy') btn.textContent = '⧉ Copy prompt';
    }, 2200);
  }

  // Step 1 — Download audit JSON
  function downloadAuditJson() {
    const json = JSON.stringify(buildJsonReport(), null, 2);
    const today = new Date().toISOString().slice(0, 10);
    const filename = 'personalization-audit-' + today + '.json';
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  }

  function handleStepDownload() {
    const filename = downloadAuditJson();
    const btn = document.getElementById('ai-step-download');
    flashStepDone(btn, '✓ Downloaded', 'downloaded');
    toast('Result + AI prompt downloaded · ' + filename);
  }

  // Step 2 — Copy prompt
  function handleStepCopy() {
    const btn = document.getElementById('ai-step-copy');
    const ok = () => {
      flashStepDone(btn, '✓ Copied', 'copied');
      toast('Prompt copied — paste it in the AI chat');
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(AI_PROMPT).then(ok).catch(() => {
        if (copyViaFallback(AI_PROMPT)) ok();
      });
    } else {
      if (copyViaFallback(AI_PROMPT)) ok();
    }
  }

  // Step 3 — Open AI tab
  function handleStepOpen() {
    if (!activeAiProvider) return;
    const cfg = AI_PROVIDERS[activeAiProvider];
    window.open(cfg.url, '_blank', 'noopener');
    toast(cfg.name + ' opened in a new tab');
  }

  function copyViaFallback(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }

  function exportResultsJson() {
    const filename = downloadAuditJson();
    toast('Results exported · ' + filename);
  }

  // ========== GLOSSARY ==========
  function renderGlossary() {
    const c = document.getElementById('gloss-content');
    let html = '';
    GLOSSARY.forEach(section => {
      html += `<div class="gloss-section" data-section="${section.section}">`;
      html += `<div class="gloss-section-head">${section.section}</div>`;
      html += `<div class="gloss-grid">`;
      section.terms.forEach(t => {
        html += `<div class="gloss-item" data-term="${escapeAttr(t.term.toLowerCase() + ' ' + (t.alias || ''))}" data-def="${escapeAttr(t.def.toLowerCase())}">`;
        html += `<div class="gloss-term">${t.term}</div>`;
        if (t.alias) html += `<div class="gloss-alias">${t.alias}</div>`;
        html += `<div class="gloss-def">${t.def}</div>`;
        if (t.where) html += `<div class="gloss-where">${t.where}</div>`;
        html += `</div>`;
      });
      html += `</div></div>`;
    });
    c.innerHTML = html;
  }

  function setupGlossarySearch() {
    const input = document.getElementById('gloss-search');
    const content = document.getElementById('gloss-content');
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      let visibleSections = new Set();
      content.querySelectorAll('.gloss-item').forEach(el => {
        const t = el.dataset.term || '';
        const d = el.dataset.def || '';
        const matches = !q || t.includes(q) || d.includes(q);
        el.classList.toggle('hidden', !matches);
        if (matches) {
          const sec = el.closest('.gloss-section');
          if (sec) visibleSections.add(sec.dataset.section);
        }
      });
      content.querySelectorAll('.gloss-section').forEach(sec => {
        sec.style.display = (q && !visibleSections.has(sec.dataset.section)) ? 'none' : '';
      });
      // Empty state
      const anyVisible = q ? visibleSections.size > 0 : true;
      let emptyEl = document.getElementById('gloss-empty');
      if (!anyVisible && !emptyEl) {
        emptyEl = document.createElement('div');
        emptyEl.id = 'gloss-empty';
        emptyEl.className = 'gloss-empty';
        emptyEl.textContent = `No terms match "${q}". Try a different word or browse the sections.`;
        content.appendChild(emptyEl);
      } else if (anyVisible && emptyEl) {
        emptyEl.remove();
      } else if (!anyVisible && emptyEl) {
        emptyEl.textContent = `No terms match "${q}". Try a different word or browse the sections.`;
      }
    });
  }

  // Tab order for prev/next navigation (matches the order of .tab elements in the DOM)
  const TAB_ORDER = [
    { id: 'intake',     label: 'Context'   },
    { id: 'checklist',  label: 'Checklist' },
    { id: 'scorecard',  label: 'Scorecard' },
    { id: 'evaluation', label: 'Verdict'   },
    { id: 'glossary',   label: 'Glossary'  },
  ];

  function getActiveTabIndex() {
    const active = document.querySelector('.tab.active');
    if (!active) return 0;
    return TAB_ORDER.findIndex(t => t.id === active.dataset.section);
  }

  function navigateSection(direction) {
    const idx = getActiveTabIndex();
    const target = idx + direction;
    if (target < 0 || target >= TAB_ORDER.length) return;
    const targetTab = document.querySelector(`[data-section="${TAB_ORDER[target].id}"]`);
    if (targetTab) targetTab.click();
  }

  function updateNavButtons() {
    const idx = getActiveTabIndex();
    const prevBtn = document.getElementById('btn-prev-section');
    const nextBtn = document.getElementById('btn-next-section');
    const prevLabel = document.getElementById('btn-prev-label');
    const nextLabel = document.getElementById('btn-next-label');

    if (idx <= 0) {
      prevBtn.classList.add('is-hidden');
    } else {
      prevBtn.classList.remove('is-hidden');
      prevLabel.textContent = TAB_ORDER[idx - 1].label;
    }
    if (idx >= TAB_ORDER.length - 1) {
      nextBtn.classList.add('is-hidden');
    } else {
      nextBtn.classList.remove('is-hidden');
      nextLabel.textContent = TAB_ORDER[idx + 1].label;
    }
  }

  function setupTabs() {
    document.getElementById('tabs').addEventListener('click', e => {
      const t = e.target.closest('.tab');
      if (!t) return;
      document.querySelectorAll('.tab').forEach(x => x.classList.remove('active'));
      t.classList.add('active');
      document.querySelectorAll('.section').forEach(s => s.classList.remove('active'));
      const targetSection = document.getElementById('section-' + t.dataset.section);
      targetSection.classList.add('active');

      // Refresh prev/next button labels for the new section
      updateNavButtons();

      // Scroll so the section starts just below the sticky progress bar.
      // We measure the actual sticky chrome height to be accurate at any zoom level.
      const nav = document.querySelector('.nav');
      const tabs = document.querySelector('.tabs-wrap');
      const prog = document.querySelector('.progress-wrap');
      const stickyHeight = (nav?.offsetHeight || 0) + (tabs?.offsetHeight || 0) + (prog?.offsetHeight || 0);
      const targetY = targetSection.getBoundingClientRect().top + window.pageYOffset - stickyHeight - 16;
      window.scrollTo({ top: Math.max(targetY, 0), behavior: 'smooth' });
    });
  }

  function setupActions() {
    // Mobile hamburger menu
    const burger = document.getElementById('nav-burger');
    const navActions = document.getElementById('nav-actions');
    burger.addEventListener('click', () => {
      const isOpen = navActions.classList.toggle('open');
      burger.classList.toggle('open', isOpen);
      burger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    // Close menu when an action button is tapped
    navActions.addEventListener('click', (e) => {
      if (e.target.closest('button')) {
        navActions.classList.remove('open');
        burger.classList.remove('open');
        burger.setAttribute('aria-expanded', 'false');
      }
    });
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!navActions.classList.contains('open')) return;
      if (!navActions.contains(e.target) && !burger.contains(e.target)) {
        navActions.classList.remove('open');
        burger.classList.remove('open');
        burger.setAttribute('aria-expanded', 'false');
      }
    });

    document.getElementById('btn-export-results').addEventListener('click', exportResultsJson);
    document.getElementById('btn-review-claude').addEventListener('click', () => sendToAI('claude'));
    document.getElementById('btn-review-chatgpt').addEventListener('click', () => sendToAI('chatgpt'));
    document.getElementById('btn-review-gemini').addEventListener('click', () => sendToAI('gemini'));
    document.getElementById('ai-step-download').addEventListener('click', handleStepDownload);
    var stepCopy = document.getElementById('ai-step-copy'); if (stepCopy) stepCopy.addEventListener('click', handleStepCopy);
    document.getElementById('ai-step-open').addEventListener('click', handleStepOpen);
    document.getElementById('ai-modal-cancel').addEventListener('click', () => {
      activeAiProvider = null;
      document.getElementById('ai-modal').classList.remove('show');
    });
    document.getElementById('ai-modal').addEventListener('click', (e) => {
      if (e.target.id === 'ai-modal') {
        activeAiProvider = null;
        document.getElementById('ai-modal').classList.remove('show');
      }
    });
    document.getElementById('btn-prev-section').addEventListener('click', () => navigateSection(-1));
    document.getElementById('btn-next-section').addEventListener('click', () => navigateSection(+1));
    document.getElementById('btn-reset').addEventListener('click', () => {
      document.getElementById('reset-modal').classList.add('show');
    });
    document.getElementById('reset-cancel').addEventListener('click', () => {
      document.getElementById('reset-modal').classList.remove('show');
    });
    document.getElementById('reset-confirm').addEventListener('click', async () => {
      state = {
        dataState: null, customerModel: null,
        markets: { eu: null, ca: null, multi: null, other_intl: null },
        aov: null,
        stack: { esp: null, sms: null, loyalty: null, quiz: null, cdp: null, recs: null },
        metrics: {}, statuses: {}, suggestedStatuses: {},
        evidence: {}, notes: {}, expanded: {}, activeHorizon: 'today'
      };
      try { if (window.storage) await window.storage.delete(STORAGE_KEY); } catch(e) {}
      document.getElementById('reset-modal').classList.remove('show');
      renderIntake();
      renderChecklist();
      updateAllSummaries();
      toast('Reset complete');
    });
    document.getElementById('btn-accept-suggestions').addEventListener('click', () => {
      Object.keys(state.suggestedStatuses).forEach(id => {
        if (!state.statuses[id]) state.statuses[id] = state.suggestedStatuses[id];
      });
      state.suggestedStatuses = {};
      debouncedSave();
      renderChecklist();
      updateAllSummaries();
      toast(`Accepted suggestions`);
    });
  }

  async function init() {
    await loadState();
    if (!state.markets) state.markets = { eu: null, ca: null, multi: null, other_intl: null };
    if (state.markets && !('other_intl' in state.markets)) state.markets.other_intl = null;
    if (!state.stack) state.stack = { esp: null, sms: null, loyalty: null, quiz: null, cdp: null, recs: null };
    if (!state.metrics) state.metrics = {};
    if (!state.suggestedStatuses) state.suggestedStatuses = {};
    recomputeSuggestions();
    renderIntake();
    renderChecklist();
    renderGlossary();
    setupGlossarySearch();
    setupTabs();
    setupActions();
    setupListeners();
    setupScrollTop();
    updateAllSummaries();
    updateNavButtons();
  }

  // Show scroll-to-top button after the user has scrolled past the hero
  function setupScrollTop() {
    const btn = document.getElementById('scroll-top-btn');
    if (!btn) return;
    const threshold = 400;
    let ticking = false;
    function check() {
      btn.classList.toggle('visible', window.pageYOffset > threshold);
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(check);
        ticking = true;
      }
    }, { passive: true });
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    check();
  }

  // Register all delegated listeners ONCE on persistent container elements.
  // The render functions only update DOM content — they never re-attach listeners.
  function setupListeners() {

    // ----- Markets toggle (section 3) -----
    document.getElementById('markets-toggles').addEventListener('click', e => {
      const btn = e.target.closest('[data-mkt]');
      if (!btn) return;
      const id = btn.dataset.mkt, val = btn.dataset.val;
      state.markets[id] = state.markets[id] === val ? null : val;
      debouncedSave();
      renderIntake();
      renderChecklist();
      updateAllSummaries();
    });

    // ----- Stack toggle (section 5) -----
    document.getElementById('stack-toggles').addEventListener('click', e => {
      const btn = e.target.closest('[data-stack]');
      if (!btn) return;
      const id = btn.dataset.stack, val = btn.dataset.val;
      state.stack[id] = state.stack[id] === val ? null : val;
      debouncedSave();
      recomputeSuggestions();
      renderIntake();
      renderChecklist();
      updateAllSummaries();
    });

    // ----- Performance metrics (section 6) -----
    const ml = document.getElementById('metrics-list');
    ml.addEventListener('input', e => {
      const id = e.target.dataset.metric;
      if (!id) return;
      const v = e.target.value.replace(/[^0-9.]/g, '');
      state.metrics[id] = { value: v === '' ? null : parseFloat(v), skipped: false };
      debouncedSave();
      updateAllSummaries();
    });
    ml.addEventListener('click', e => {
      const btn = e.target.closest('[data-skip]');
      if (!btn) return;
      const id = btn.dataset.skip;
      const cur = state.metrics[id] || {};
      const skipping = !cur.skipped;
      state.metrics[id] = { value: null, skipped: skipping };
      debouncedSave();
      renderIntake();
      updateAllSummaries();
    });

    // ----- Checklist (status pills, evidence textareas, category collapse) -----
    const c = document.getElementById('categories-container');
    c.addEventListener('click', e => {
      const pill = e.target.closest('[data-status-id]');
      if (pill) {
        const id = pill.dataset.statusId;
        const status = pill.dataset.status;
        if (state.statuses[id] === status) {
          delete state.statuses[id];
        } else {
          state.statuses[id] = status;
        }
        if (state.suggestedStatuses[id]) delete state.suggestedStatuses[id];
        debouncedSave();
        rerenderCheckpoint(id);
        updateAllSummaries();
        return;
      }
      const head = e.target.closest('[data-toggle-cat]');
      if (head) {
        const id = head.dataset.toggleCat;
        document.getElementById('catbody-' + id).classList.toggle('collapsed');
        return;
      }
      const text = e.target.closest('.check-text');
      if (text) {
        const cp = text.closest('.check-row');
        if (!cp.classList.contains('has-status')) return;
        const id = cp.dataset.cp;
        state.expanded[id] = !state.expanded[id];
        cp.classList.toggle('expanded');
        cp.querySelector('.check-extra').classList.toggle('visible');
        debouncedSave();
      }
    });
    c.addEventListener('input', e => {
      const ev = e.target.dataset.evidence;
      if (ev) { state.evidence[ev] = e.target.value; debouncedSave(); }
    });
  }

  init();
